// ---------------------------------
// client/src/pages/AdminPdfTemplateBuilder.jsx
// PDF template WYSIWYG builder
// ---------------------------------
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useNavigate, useParams } from 'react-router-dom';
import { Layer, Rect, Stage, Transformer, Image as KonvaImage, Label, Tag, Text } from 'react-konva';
import { fetchPdfTemplate, savePdfTemplateSchema } from '../utils/pdfTemplates';
import { getPdfPageSize, loadPdfDocument, renderPdfPage } from '../utils/pdfjs';
import { createEditorPreset, deleteEditorPreset, fetchEditorPresets } from '../utils/editorPresets';
import { saveBlob } from '../utils/download';
import {
    buildFieldsFromPresetPayload,
    buildPastedFields,
    buildPresetPayload,
    buildSelectionClipboardPayload,
    readSelectionClipboardPayload,
    writeSelectionClipboardPayload,
} from '../utils/editorClipboard';
import '../css/pdfTemplateBuilder.css';

const GRID_SIZE = 4;
const HISTORY_LIMIT = 50;
const MIN_SIZE = 8;
const MIN_ZOOM = 0.9;
const MAX_ZOOM = 3;
const SELECTION_CLICK_THRESHOLD = 4;

const FIELD_TYPES = [
    { type: 'text', label: 'טקסט' },
    { type: 'number', label: 'מספר' },
    { type: 'dropdown', label: 'רשימה נפתחת' },
    { type: 'checkbox', label: 'תיבת סימון' },
    { type: 'date', label: 'תאריך' },
    { type: 'time', label: 'שעה' },
    { type: 'signature', label: 'חתימה' },
    { type: 'image', label: 'תמונה' },
    { type: 'rectangle', label: 'מלבן' },
];

const FIELD_TYPE_LABELS = {
    text: 'טקסט',
    number: 'מספר',
    dropdown: 'רשימה נפתחת',
    checkbox: 'תיבת סימון',
    date: 'תאריך',
    time: 'שעה',
    signature: 'חתימה',
    image: 'תמונה',
    rectangle: 'מלבן',
};

const DEFAULT_SIZES = {
    text: { width: 140, height: 20 },
    number: { width: 110, height: 32 },
    dropdown: { width: 150, height: 32 },
    checkbox: { width: 24, height: 24 },
    date: { width: 130, height: 32 },
    time: { width: 110, height: 32 },
    signature: { width: 220, height: 90 },
    image: { width: 160, height: 120 },
    rectangle: { width: 160, height: 80 },
};

const RECTANGLE_DEFAULT_STYLE = {
    strokeColor: '#ffffff',
    fillColor: '#ffffff',
    fillEnabled: true,
    strokeWidth: 1,
    opacity: 1,
};

const TEXT = {
    back: 'חזור',
    save: 'שמור תבנית',
    saving: 'שומר...',
    preview: 'תצוגה מקדימה',
    exportElements: 'יצוא שדות',
    importElements: 'יבוא שדות',
    exportCopied: 'התוכן הועתק ללוח הגזירה.',
    exportTitle: 'יצוא שדות',
    exportCopy: 'העתק',
    importTitle: 'יבוא שדות',
    importHint: 'הדביקו קוד JSON של שדות וקטעים.',
    importPlaceholder: 'הדביקו כאן את JSON הייצוא...',
    importAction: 'יבוא',
    importError: 'לא ניתן ליבא את הקוד.',
    importSuccess: 'השדות יובאו בהצלחה.',
    close: 'סגור',
    tools: 'כלים',
    presets: 'אלמנטים שמורים',
    savedElements: 'אלמנטים שמורים',
    saveCurrentSelection: 'שמור את הבחירה הנוכחית',
    presetName: 'שם האלמנט',
    savePreset: 'שמור אלמנט',
    savingPreset: 'שומר...',
    noSavedElements: 'עדיין אין אלמנטים שמורים.',
    insertPreset: 'הוסף',
    deletePreset: 'מחק',
    presetFields: 'שדות',
    sections: 'קטעים',
    properties: 'מאפיינים',
    select: 'בחירה',
    multiSelect: 'בחירה מרובה',
    tableRow: 'שורת טבלה',
    addSection: 'הוסף קטע',
    fit: 'התאם לרוחב',
    fullSize: '100%',
    skipSequence: 'דלג על רצף',
    undo: 'בטל',
    redo: 'בצע שוב',
    zoomIn: 'הגדל',
    zoomOut: 'הקטן',
    zoom: 'זום',
    page: 'עמוד',
    pageOf: 'מתוך',
    prevPage: 'קודם',
    nextPage: 'הבא',
    collapseTools: 'צמצם כלים',
    expandTools: 'הרחב כלים',
    collapseProps: 'צמצם מאפיינים',
    expandProps: 'הרחב מאפיינים',
    emptySelection: 'בחרו שדה כדי להציג מאפיינים.',
    loading: 'טוען PDF...',
    error: 'אירעה שגיאה בטעינת ה-PDF.',
    fieldLabel: 'תווית שדה',
    fieldKey: 'מפתח שדה',
    fieldRequired: 'שדה חובה',
    placeholder: 'מציין מקום',
    suggestedValues: 'ערכים מוצעים',
    fontSize: 'גודל גופן',
    fontSizeMode: 'מצב גודל גופן',
    fontSizeModeManual: 'ידני',
    fontSizeModeAuto: 'אוטומטי',
    wrapMode: 'מצב גלישת טקסט',
    wrapModeSameLine: 'אותה שורה',
    wrapModeManualLines: 'שורות ידניות בלבד',
    wrapModeSameWord: 'שבירה בין מילים בלבד',
    wrapModeNewWords: 'שבירה גם בתוך מילים ארוכות',
    rangeMode: 'מצב טווח',
    rangeModeSingle: 'ערך יחיד',
    rangeModeRange: 'טווח',
    align: 'יישור',
    direction: 'כיוון',
    alignLeft: 'שמאל',
    alignCenter: 'מרכז',
    alignRight: 'ימין',
    alignTop: 'עליון',
    alignMiddle: 'אמצע',
    alignBottom: 'תחתון',
    distributeX: 'פיזור אופקי',
    distributeY: 'פיזור אנכי',
    matchWidth: 'השוואת רוחב',
    matchHeight: 'השוואת גובה',
    options: 'אפשרויות (כל אפשרות בשורה חדשה)',
    numericConfig: 'הגדרות מספר',
    min: 'מינימום',
    max: 'מקסימום',
    decimals: 'ספרות אחרי הנקודה',
    section: 'קטע',
    deleteSection: 'מחיקת קטע',
    sequenceNumber: 'מספר רצף',
    autoFill: 'מילוי אוטומטי',
    multiline: 'רב-שורה',
    readOnly: 'קריאה בלבד',
    staticValue: 'ערך קבוע',
    imageAsset: 'תמונה שנבחרה',
    imageUpload: 'העלאת תמונה',
    imageClear: 'הסרת תמונה',
    rectStrokeColor: 'צבע קו',
    rectStrokeWidth: 'עובי קו',
    rectFill: 'מילוי',
    rectFillColor: 'צבע מילוי',
    rectOpacity: 'אטימות',
    position: 'מיקום',
    size: 'גודל',
    layer: 'שכבה',
    bringToFront: 'העבר קדימה',
    sendToBack: 'העבר אחורה',
    tableRows: 'שורות טבלה',
    rowCount: 'מספר שורות',
    rowSpacing: 'מרווח שורות',
    applyRows: 'החל שורות',
    x: 'X',
    y: 'Y',
    width: 'רוחב',
    height: 'גובה',
    mixed: 'מעורב',
    activeSection: 'קטע פעיל',
    exportPresets: 'ייצוא אלמנטים שמורים',
    importPresets: 'ייבוא אלמנטים שמורים',
    presetImportError: 'לא ניתן לייבא את האלמנטים השמורים.',
    presetImportSuccess: 'האלמנטים השמורים יובאו בהצלחה.',
    presetExportSuccess: 'האלמנטים השמורים יוצאו לקובץ.',
};

const ALIGN_OPTIONS = [
    { value: '', label: 'אוטומטי' },
    { value: 'left', label: 'שמאל' },
    { value: 'center', label: 'מרכז' },
    { value: 'right', label: 'ימין' },
];

const DIRECTION_OPTIONS = [
    { value: 'auto', label: 'אוטומטי' },
    { value: 'ltr', label: 'שמאל לימין' },
    { value: 'rtl', label: 'ימין לשמאל' },
];

const ALIGN_LABEL_MAP = ALIGN_OPTIONS.reduce((acc, option) => {
    if (option.value) {
        acc[option.value] = option.label;
    }
    return acc;
}, {});

const AUTO_FILL_OPTIONS = {
    text: [
        { value: '', label: 'ללא' },
        { value: 'job_number', label: 'מספר עבודה' },
        { value: 'worker_name', label: 'שם עובד' },
        { value: 'worker_role', label: 'תפקיד עובד' },
        { value: 'recipient_company', label: 'שם החברה המקבלת שירות' },
    ],
    date: [
        { value: '', label: 'ללא' },
        { value: 'date_today', label: 'תאריך היום' },
        { value: 'date_form_start', label: 'תאריך תחילת הטופס' },
        { value: 'date_on_click', label: 'תאריך בלחיצה' },
    ],
    time: [
        { value: '', label: 'ללא' },
        { value: 'time_now', label: 'שעה נוכחית' },
        { value: 'time_form_start', label: 'שעת תחילת הטופס' },
        { value: 'time_on_click', label: 'שעה בלחיצה' },
    ],
};

const TEXT_STYLE_TYPES = new Set(['text', 'number', 'dropdown', 'date', 'time', 'checkbox']);
const PLACEHOLDER_TYPES = new Set(['text', 'number']);
const AUTO_FILL_TYPES = new Set(['text', 'date', 'time']);
const REQUIRED_TYPES = new Set(['text', 'number', 'dropdown', 'checkbox', 'date', 'time', 'signature']);
const LAYERABLE_TYPES = new Set(['image', 'rectangle']);
const TABLE_ROW_GROUP_KEY = 'tableRowGroupId';
const TABLE_ROW_INDEX_KEY = 'tableRowIndex';
const TABLE_ROW_BASE_KEY = 'tableRowBaseId';
const DIRTY_PROMPT_MESSAGE = 'יש שינויים שלא נשמרו. לצאת בלי לשמור?';

function createId() {
    if (typeof crypto !== 'undefined') {
        if (crypto.randomUUID) {
            return crypto.randomUUID();
        }
        if (crypto.getRandomValues) {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));
            return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex.slice(10).join('')}`;
        }
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const rand = Math.random() * 16 | 0;
        const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
        return value.toString(16);
    });
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function resolveNativeEvent(event) {
    return event?.evt || event?.nativeEvent || event;
}

function resolveClientPoint(nativeEvent) {
    if (!nativeEvent) return null;
    if (nativeEvent.touches && nativeEvent.touches.length > 0) {
        return { x: nativeEvent.touches[0].clientX, y: nativeEvent.touches[0].clientY };
    }
    if (nativeEvent.changedTouches && nativeEvent.changedTouches.length > 0) {
        return { x: nativeEvent.changedTouches[0].clientX, y: nativeEvent.changedTouches[0].clientY };
    }
    if (typeof nativeEvent.clientX === 'number' && typeof nativeEvent.clientY === 'number') {
        return { x: nativeEvent.clientX, y: nativeEvent.clientY };
    }
    return null;
}

function snapValue(value) {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

function normalizeOptions(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.filter(Boolean);
            }
        } catch {
            // fall through to line split
        }
        return trimmed
            .split(/\r?\n|,/)
            .map((entry) => entry.trim())
            .filter(Boolean);
    }
    return [];
}

function normalizeNumericConfig(value) {
    if (!value) return {};
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    if (typeof value === 'object') {
        return value;
    }
    return {};
}

function normalizeRectStyle(value) {
    if (!value) return { ...RECTANGLE_DEFAULT_STYLE };
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return { ...RECTANGLE_DEFAULT_STYLE, ...parsed };
            }
        } catch {
            return { ...RECTANGLE_DEFAULT_STYLE };
        }
    }
    if (typeof value === 'object') {
        return { ...RECTANGLE_DEFAULT_STYLE, ...value };
    }
    return { ...RECTANGLE_DEFAULT_STYLE };
}

function normalizeField(field, pageSize) {
    const xNorm = Number(field.xNorm);
    const yNorm = Number(field.yNorm);
    const wNorm = Number(field.wNorm);
    const hNorm = Number(field.hNorm);
    if (!Number.isFinite(xNorm) || !Number.isFinite(yNorm) || !Number.isFinite(wNorm) || !Number.isFinite(hNorm)) {
        return null;
    }
    if (wNorm <= 0 || hNorm <= 0) {
        return null;
    }
    return {
        x: xNorm * pageSize.width,
        y: yNorm * pageSize.height,
        width: wNorm * pageSize.width,
        height: hNorm * pageSize.height,
    };
}

function cloneState(data) {
    return JSON.parse(JSON.stringify(data));
}

function parsePresetPayloadValue(value) {
    if (!value) return null;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return typeof value === 'object' ? value : null;
}

function useBrowserNavigationBlocker(when, message, bypassRef) {
    const navigationContext = useContext(UNSAFE_NavigationContext);

    useEffect(() => {
        const navigator = navigationContext?.navigator;
        if (!when || !navigator || typeof navigator.block !== 'function') {
            return undefined;
        }

        const unblock = navigator.block((tx) => {
            if (bypassRef?.current) {
                bypassRef.current = false;
                unblock();
                tx.retry();
                return;
            }
            if (!window.confirm(message)) {
                return;
            }
            unblock();
            tx.retry();
        });

        return unblock;
    }, [bypassRef, message, navigationContext, when]);
}

export default function AdminPdfTemplateBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const stageRef = useRef(null);
    const transformerRef = useRef(null);
    const containerRef = useRef(null);
    const presetImportInputRef = useRef(null);
    const pointerState = useRef({ pointers: new Map(), lastCenter: null, lastDistance: 0 });
    const panState = useRef({ isPanning: false, startX: 0, startY: 0, origin: { x: 0, y: 0 } });
    const shapeRefs = useRef({});
    const imageCacheRef = useRef({});
    const dragGroupRef = useRef(null);
    const lastCanvasPointerRef = useRef(null);
    const navigationBypassRef = useRef(false);
    const hasPopstateGuardRef = useRef(false);
    const isCleaningPopstateGuardRef = useRef(false);
    const allowNextPopLeaveRef = useRef(false);

    const [template, setTemplate] = useState(null);
    const [fields, setFields] = useState([]);
    const [sections, setSections] = useState([]);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeTool, setActiveTool] = useState('select');
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pageCanvas, setPageCanvas] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
    const [fitScale, setFitScale] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectionRect, setSelectionRect] = useState({ x: 0, y: 0, width: 0, height: 0, visible: false });
    const [rectInputs, setRectInputs] = useState({ x: '', y: '', width: '', height: '' });
    const [spacingInputs, setSpacingInputs] = useState({ x: '', y: '' });
    const [tableRowConfig, setTableRowConfig] = useState({ count: '2', spacing: '24' });
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [exportPayload, setExportPayload] = useState('');
    const [exportFeedback, setExportFeedback] = useState('');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importPayload, setImportPayload] = useState('');
    const [importError, setImportError] = useState('');
    const [importFeedback, setImportFeedback] = useState('');
    const [activeSidebarTab, setActiveSidebarTab] = useState('tools');
    const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
    const [isRightCollapsed, setIsRightCollapsed] = useState(false);
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);
    const [presets, setPresets] = useState([]);
    const [presetName, setPresetName] = useState('');
    const [presetError, setPresetError] = useState('');
    const [presetFeedback, setPresetFeedback] = useState('');
    const [isPresetSaving, setIsPresetSaving] = useState(false);
    const [isPresetImporting, setIsPresetImporting] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [, setHistory] = useState({ past: [], future: [] });
    const savedSnapshotRef = useRef('');
    const dirtyStateRef = useRef(false);

    const snapEnabled = true;

    const pageFields = useMemo(
        () =>
            fields
                .filter((field) => field.pageIndex === currentPage)
                .slice()
                .sort((a, b) => (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0)),
        [fields, currentPage],
    );
    const pageCount = pdfDoc?.numPages || 1;

    const selectedFields = useMemo(
        () => fields.filter((field) => selectedIds.includes(field.id)),
        [fields, selectedIds],
    );
    const singleField = selectedFields[0] || null;
    const singleSelectedType = singleField?.type;
    const supportsPlaceholder = PLACEHOLDER_TYPES.has(singleSelectedType);
    const supportsTextStyle = TEXT_STYLE_TYPES.has(singleSelectedType);
    const supportsAutoFill = AUTO_FILL_TYPES.has(singleSelectedType);
    const supportsTextOptions = singleSelectedType === 'text';
    const isSingleReadOnlyText = singleSelectedType === 'text' && Boolean(singleField?.isReadOnly);
    const singleSupportsRequired = Boolean(singleSelectedType && REQUIRED_TYPES.has(singleSelectedType));
    const singleSupportsSequence = singleSupportsRequired && !isSingleReadOnlyText;
    const multiSupportsTextStyle =
        selectedFields.length > 1 && selectedFields.every((field) => TEXT_STYLE_TYPES.has(field.type));
    const multiSupportsRequired =
        selectedFields.length > 1 && selectedFields.every((field) => REQUIRED_TYPES.has(field.type));
    const multiSupportsPlaceholder =
        selectedFields.length > 1 && selectedFields.every((field) => PLACEHOLDER_TYPES.has(field.type));
    const multiSupportsTextOptions =
        selectedFields.length > 1 && selectedFields.every((field) => field.type === 'text');
    const multiSupportsSequence =
        selectedFields.length > 1 &&
        selectedFields.every(
            (field) => REQUIRED_TYPES.has(field.type) && !(field.type === 'text' && field.isReadOnly),
        );
    const multiSupportsSection = selectedFields.length > 1;
    const supportsLayerOrdering =
        selectedFields.length > 0 && selectedFields.every((field) => LAYERABLE_TYPES.has(field.type));
    const activeTableRowGroupId = useMemo(() => {
        if (selectedFields.length === 0) return '';
        const groupId = selectedFields[0]?.[TABLE_ROW_GROUP_KEY];
        if (!groupId) return '';
        const isSameGroup = selectedFields.every((field) => field[TABLE_ROW_GROUP_KEY] === groupId);
        return isSameGroup ? String(groupId) : '';
    }, [selectedFields]);
    const hasActiveTableRowGroup = Boolean(activeTableRowGroupId);
    const selectedRects = useMemo(() => {
        if (!pageSize.width || !pageSize.height || selectedFields.length === 0) {
            return [];
        }
        return selectedFields
            .map((field) => {
                const rect = normalizeField(field, pageSize);
                return rect ? { id: field.id, rect } : null;
            })
            .filter(Boolean);
    }, [selectedFields, pageSize]);
    const selectionBounds = useMemo(() => {
        if (selectedRects.length === 0) return null;
        const minX = Math.min(...selectedRects.map((item) => item.rect.x));
        const minY = Math.min(...selectedRects.map((item) => item.rect.y));
        const maxX = Math.max(...selectedRects.map((item) => item.rect.x + item.rect.width));
        const maxY = Math.max(...selectedRects.map((item) => item.rect.y + item.rect.height));
        return { minX, minY, maxX, maxY };
    }, [selectedRects]);
    const canAlignSelection = selectedRects.length > 1;
    const canDistributeSelection = selectedRects.length > 2;
    const canSpaceSelection = selectedRects.length > 1;
    const isSelectTool = activeTool === 'select' || activeTool === 'tableRow';
    const parsedRowCount = Number(tableRowConfig.count);
    const parsedRowSpacing = Number(tableRowConfig.spacing);
    const canApplyTableRows =
        activeTool === 'tableRow' &&
        selectedFields.length > 0 &&
        Number.isFinite(parsedRowCount) &&
        parsedRowCount >= 2 &&
        Number.isFinite(parsedRowSpacing);
    const selectedRectStyle =
        selectedFields.length === 1 && selectedFields[0].type === 'rectangle'
            ? normalizeRectStyle(selectedFields[0].styleJson)
            : RECTANGLE_DEFAULT_STYLE;
    const builderSnapshot = useMemo(
        () =>
            JSON.stringify({
                fields: cloneState(fields),
                sections: cloneState(sections),
                activeSectionId,
            }),
        [activeSectionId, fields, sections],
    );
    useBrowserNavigationBlocker(isDirty, DIRTY_PROMPT_MESSAGE, navigationBypassRef);
    useEffect(() => {
        if (selectedFields.length !== 1 || !pageSize.width || !pageSize.height) {
            setRectInputs({ x: '', y: '', width: '', height: '' });
            return;
        }
        const field = selectedFields[0];
        const toPx = (value, size) =>
            Number.isFinite(Number(value)) ? String(Math.round(Number(value) * size)) : '';
        setRectInputs({
            x: toPx(field.xNorm, pageSize.width),
            y: toPx(field.yNorm, pageSize.height),
            width: toPx(field.wNorm, pageSize.width),
            height: toPx(field.hNorm, pageSize.height),
        });
    }, [selectedFields, pageSize]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }
        const media = window.matchMedia('(pointer: coarse)');
        const update = () => setIsCoarsePointer(media.matches);
        update();
        if (media.addEventListener) {
            media.addEventListener('change', update);
        } else {
            media.addListener(update);
        }
        return () => {
            if (media.removeEventListener) {
                media.removeEventListener('change', update);
            } else {
                media.removeListener(update);
            }
        };
    }, []);

    useEffect(() => {
        setIsDirty(savedSnapshotRef.current !== '' && builderSnapshot !== savedSnapshotRef.current);
    }, [builderSnapshot]);

    useEffect(() => {
        dirtyStateRef.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (isDirty) {
            if (!hasPopstateGuardRef.current) {
                window.history.pushState(
                    {
                        ...(window.history.state || {}),
                        __pdfBuilderDirtyGuard: String(id || 'builder'),
                    },
                    '',
                    window.location.href,
                );
                hasPopstateGuardRef.current = true;
            }
            return;
        }
        if (hasPopstateGuardRef.current) {
            isCleaningPopstateGuardRef.current = true;
            window.history.back();
        }
    }, [id, isDirty]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    useEffect(() => {
        const handlePopState = () => {
            if (isCleaningPopstateGuardRef.current) {
                isCleaningPopstateGuardRef.current = false;
                hasPopstateGuardRef.current = false;
                return;
            }
            if (allowNextPopLeaveRef.current) {
                allowNextPopLeaveRef.current = false;
                hasPopstateGuardRef.current = false;
                return;
            }
            if (!dirtyStateRef.current || !hasPopstateGuardRef.current) {
                return;
            }
            if (window.confirm(DIRTY_PROMPT_MESSAGE)) {
                navigationBypassRef.current = true;
                allowNextPopLeaveRef.current = true;
                hasPopstateGuardRef.current = false;
                window.history.back();
                return;
            }
            window.history.pushState(
                {
                    ...(window.history.state || {}),
                    __pdfBuilderDirtyGuard: String(id || 'builder'),
                },
                '',
                window.location.href,
            );
            hasPopstateGuardRef.current = true;
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [id]);

    const pushHistory = useCallback(() => {
        setHistory((prev) => {
            const snapshot = {
                fields: cloneState(fields),
                sections: cloneState(sections),
                activeSectionId,
                selectedIds: cloneState(selectedIds),
            };
            const past = [...prev.past, snapshot].slice(-HISTORY_LIMIT);
            return { past, future: [] };
        });
    }, [fields, sections, activeSectionId, selectedIds]);

    const updateSelectedFields = useCallback(
        (updates) => {
            if (selectedIds.length === 0) return;
            pushHistory();
            setFields((prev) =>
                prev.map((field) =>
                    selectedIds.includes(field.id) ? { ...field, ...updates } : field,
                ),
            );
        },
        [pushHistory, selectedIds],
    );

    const applySelectionRects = useCallback(
        (rectMap) => {
            if (!rectMap || rectMap.size === 0 || !pageSize.width || !pageSize.height) {
                return;
            }
            pushHistory();
            setFields((prev) =>
                prev.map((field) => {
                    const nextRect = rectMap.get(field.id);
                    if (!nextRect) return field;
                    return {
                        ...field,
                        xNorm: nextRect.x / pageSize.width,
                        yNorm: nextRect.y / pageSize.height,
                        wNorm: nextRect.width / pageSize.width,
                        hNorm: nextRect.height / pageSize.height,
                    };
                }),
            );
        },
        [pageSize, pushHistory],
    );

    const alignSelection = useCallback(
        (mode) => {
            if (!selectionBounds || selectedRects.length < 2 || !pageSize.width || !pageSize.height) {
                return;
            }
            const { minX, minY, maxX, maxY } = selectionBounds;
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;
            const updates = new Map();
            selectedRects.forEach(({ id, rect }) => {
                let x = rect.x;
                let y = rect.y;
                if (mode === 'left') x = minX;
                if (mode === 'centerX') x = centerX - rect.width / 2;
                if (mode === 'right') x = maxX - rect.width;
                if (mode === 'top') y = minY;
                if (mode === 'centerY') y = centerY - rect.height / 2;
                if (mode === 'bottom') y = maxY - rect.height;
                if (snapEnabled) {
                    x = snapValue(x);
                    y = snapValue(y);
                }
                x = clamp(x, 0, pageSize.width - rect.width);
                y = clamp(y, 0, pageSize.height - rect.height);
                updates.set(id, { ...rect, x, y });
            });
            applySelectionRects(updates);
        },
        [applySelectionRects, pageSize, selectionBounds, selectedRects, snapEnabled],
    );

    const distributeSelection = useCallback(
        (axis) => {
            if (!selectionBounds || selectedRects.length < 3 || !pageSize.width || !pageSize.height) {
                return;
            }
            const sorted = [...selectedRects].sort((a, b) =>
                axis === 'x' ? a.rect.x - b.rect.x : a.rect.y - b.rect.y,
            );
            const min = axis === 'x' ? selectionBounds.minX : selectionBounds.minY;
            const max = axis === 'x' ? selectionBounds.maxX : selectionBounds.maxY;
            const totalSize = sorted.reduce(
                (sum, item) => sum + (axis === 'x' ? item.rect.width : item.rect.height),
                0,
            );
            const gap = (max - min - totalSize) / (sorted.length - 1);
            let cursor = min;
            const updates = new Map();
            sorted.forEach((item) => {
                let x = item.rect.x;
                let y = item.rect.y;
                if (axis === 'x') {
                    x = cursor;
                    cursor += item.rect.width + gap;
                } else {
                    y = cursor;
                    cursor += item.rect.height + gap;
                }
                if (snapEnabled) {
                    x = snapValue(x);
                    y = snapValue(y);
                }
                x = clamp(x, 0, pageSize.width - item.rect.width);
                y = clamp(y, 0, pageSize.height - item.rect.height);
                updates.set(item.id, { ...item.rect, x, y });
            });
            applySelectionRects(updates);
        },
        [applySelectionRects, pageSize, selectionBounds, selectedRects, snapEnabled],
    );

    const spaceSelection = useCallback(
        (axis, rawSpacing) => {
            if (!selectionBounds || selectedRects.length < 2 || !pageSize.width || !pageSize.height) {
                return;
            }
            const parsed = Number(rawSpacing);
            if (!Number.isFinite(parsed)) return;
            const spacing = Math.max(0, parsed);
            const sorted = [...selectedRects].sort((a, b) =>
                axis === 'x' ? a.rect.x - b.rect.x : a.rect.y - b.rect.y,
            );
            const start = axis === 'x' ? selectionBounds.minX : selectionBounds.minY;
            const updates = new Map();
            sorted.forEach((item, index) => {
                let x = item.rect.x;
                let y = item.rect.y;
                if (axis === 'x') {
                    x = start + index * spacing;
                } else {
                    y = start + index * spacing;
                }
                x = clamp(x, 0, pageSize.width - item.rect.width);
                y = clamp(y, 0, pageSize.height - item.rect.height);
                updates.set(item.id, { ...item.rect, x, y });
            });
            applySelectionRects(updates);
        },
        [applySelectionRects, pageSize, selectionBounds, selectedRects, snapEnabled],
    );

    const handleSpacingChange = useCallback(
        (axis, value) => {
            setSpacingInputs((prev) => ({ ...prev, [axis]: value }));
            if (value === '') return;
            spaceSelection(axis, value);
        },
        [spaceSelection],
    );

    const moveSelectionLayer = useCallback(
        (direction) => {
            if (selectedIds.length === 0) return;
            pushHistory();
            setFields((prev) => {
                const pageOrder = [];
                const byPage = new Map();
                prev.forEach((field) => {
                    const pageKey = Number.isInteger(field.pageIndex) ? field.pageIndex : 0;
                    if (!byPage.has(pageKey)) {
                        byPage.set(pageKey, []);
                        pageOrder.push(pageKey);
                    }
                    byPage.get(pageKey).push(field);
                });
                const nextFields = [];
                const sortByOrder = (a, b) => (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0);
                pageOrder.forEach((pageKey) => {
                    const group = byPage.get(pageKey) || [];
                    const selected = group.filter((field) => selectedIds.includes(field.id));
                    if (selected.length === 0) {
                        nextFields.push(...group);
                        return;
                    }
                    const selectedSorted = selected.slice().sort(sortByOrder);
                    const othersSorted = group.filter((field) => !selectedIds.includes(field.id)).sort(sortByOrder);
                    const ordered =
                        direction === 'front'
                            ? [...othersSorted, ...selectedSorted]
                            : [...selectedSorted, ...othersSorted];
                    ordered.forEach((field, index) => {
                        nextFields.push({ ...field, orderIndex: index });
                    });
                });
                return nextFields;
            });
        },
        [pushHistory, selectedIds],
    );

    const updateTableRowGroup = useCallback(
        (groupId, nextConfig, { addHistory = false } = {}) => {
            if (!groupId || !pageSize.width || !pageSize.height) return;
            const parsedCount = Number(nextConfig?.count);
            const parsedSpacing = Number(nextConfig?.spacing);
            if (!Number.isFinite(parsedCount) || !Number.isFinite(parsedSpacing)) return;
            const rowCount = Math.max(1, Math.floor(parsedCount));
            const rowSpacing = Math.max(0, parsedSpacing);

            if (addHistory) {
                pushHistory();
            }

            const groupSelected = selectedFields.some((field) => field[TABLE_ROW_GROUP_KEY] === groupId);
            const currentSelectedIds = new Set(selectedIds);
            const removedIds = new Set();
            const addedIds = new Set();

            setFields((prev) => {
                const baseFields = prev.filter(
                    (field) =>
                        field[TABLE_ROW_GROUP_KEY] === groupId &&
                        Number(field[TABLE_ROW_INDEX_KEY] || 0) === 0,
                );
                if (baseFields.length === 0) {
                    return prev;
                }

                const baseRects = new Map();
                baseFields.forEach((field) => {
                    const rect = normalizeField(field, pageSize);
                    if (rect) {
                        const baseId = field[TABLE_ROW_BASE_KEY] || field.id;
                        baseRects.set(baseId, rect);
                    }
                });

                const maxOrderIndexByPage = new Map();
                let sequenceCursor = prev.reduce(
                    (maxValue, field) =>
                        field.skipSequence
                            ? maxValue
                            : Math.max(maxValue, Number(field.sequenceNumber) || 0),
                    0,
                );
                prev.forEach((field) => {
                    const pageKey = Number.isInteger(field.pageIndex) ? field.pageIndex : 0;
                    const current = maxOrderIndexByPage.get(pageKey) ?? -1;
                    const nextValue = Math.max(current, Number(field.orderIndex) || 0);
                    maxOrderIndexByPage.set(pageKey, nextValue);
                });

                const existingKeys = new Set();
                const nextFields = [];

                prev.forEach((field) => {
                    if (field[TABLE_ROW_GROUP_KEY] !== groupId) {
                        nextFields.push(field);
                        return;
                    }

                    const rowIndex = Number(field[TABLE_ROW_INDEX_KEY] || 0);
                    const baseId = field[TABLE_ROW_BASE_KEY] || field.id;

                    if (rowIndex === 0) {
                        if (
                            field[TABLE_ROW_BASE_KEY] !== baseId ||
                            field[TABLE_ROW_INDEX_KEY] !== 0
                        ) {
                            nextFields.push({
                                ...field,
                                [TABLE_ROW_GROUP_KEY]: groupId,
                                [TABLE_ROW_BASE_KEY]: baseId,
                                [TABLE_ROW_INDEX_KEY]: 0,
                            });
                        } else {
                            nextFields.push(field);
                        }
                        return;
                    }

                    const rect = baseRects.get(baseId);
                    if (!rect || rowIndex >= rowCount) {
                        removedIds.add(field.id);
                        return;
                    }

                    existingKeys.add(`${baseId}:${rowIndex}`);
                    const nextY = clamp(rect.y + rowSpacing * rowIndex, 0, pageSize.height - rect.height);
                    nextFields.push({
                        ...field,
                        [TABLE_ROW_GROUP_KEY]: groupId,
                        [TABLE_ROW_BASE_KEY]: baseId,
                        [TABLE_ROW_INDEX_KEY]: rowIndex,
                        xNorm: rect.x / pageSize.width,
                        yNorm: nextY / pageSize.height,
                        wNorm: rect.width / pageSize.width,
                        hNorm: rect.height / pageSize.height,
                    });
                });

                baseFields.forEach((field) => {
                    const baseId = field[TABLE_ROW_BASE_KEY] || field.id;
                    const rect = baseRects.get(baseId);
                    if (!rect) return;
                    for (let rowIndex = 1; rowIndex < rowCount; rowIndex += 1) {
                        const key = `${baseId}:${rowIndex}`;
                        if (existingKeys.has(key)) continue;
                        const nextOrderIndex =
                            (maxOrderIndexByPage.get(field.pageIndex) ?? -1) + 1;
                        maxOrderIndexByPage.set(field.pageIndex, nextOrderIndex);
                        const shouldSkip = Boolean(field.skipSequence);
                        const nextSequence = shouldSkip
                            ? Number.isInteger(field.sequenceNumber)
                                ? field.sequenceNumber
                                : 0
                            : (sequenceCursor += 1);
                        const nextY = clamp(
                            rect.y + rowSpacing * rowIndex,
                            0,
                            pageSize.height - rect.height,
                        );
                        const newField = {
                            ...cloneState(field),
                            id: createId(),
                            xNorm: rect.x / pageSize.width,
                            yNorm: nextY / pageSize.height,
                            wNorm: rect.width / pageSize.width,
                            hNorm: rect.height / pageSize.height,
                            orderIndex: nextOrderIndex,
                            sequenceNumber: nextSequence,
                            [TABLE_ROW_GROUP_KEY]: groupId,
                            [TABLE_ROW_BASE_KEY]: baseId,
                            [TABLE_ROW_INDEX_KEY]: rowIndex,
                        };
                        addedIds.add(newField.id);
                        nextFields.push(newField);
                    }
                });

                return nextFields;
            });

            if (removedIds.size > 0 || addedIds.size > 0) {
                setSelectedIds((prev) => {
                    const next = prev.filter((id) => !removedIds.has(id));
                    if (groupSelected) {
                        addedIds.forEach((id) => next.push(id));
                    }
                    return next;
                });
            }
        },
        [pageSize, pushHistory, selectedFields, selectedIds],
    );

    const handleCreateTableRows = useCallback(() => {
        if (!pageSize.width || !pageSize.height) return;
        const baseSelection = selectedFields.filter((field) => field.pageIndex === currentPage);
        if (baseSelection.length === 0) return;
        const parsedCount = Number(tableRowConfig.count);
        const parsedSpacing = Number(tableRowConfig.spacing);
        if (!Number.isFinite(parsedCount) || !Number.isFinite(parsedSpacing)) return;
        const rowCount = Math.max(1, Math.floor(parsedCount));
        const rowSpacing = Math.max(0, parsedSpacing);
        if (rowCount < 2) return;

        if (hasActiveTableRowGroup) {
            updateTableRowGroup(activeTableRowGroupId, tableRowConfig, { addHistory: true });
            return;
        }

        const groupId = createId();
        const baseIds = new Set(baseSelection.map((field) => field.id));

        const maxSequence = fields.reduce(
            (maxValue, field) =>
                field.skipSequence ? maxValue : Math.max(maxValue, Number(field.sequenceNumber) || 0),
            0,
        );
        let sequenceCursor = maxSequence;
        const maxOrderIndexByPage = new Map();
        fields.forEach((field) => {
            const pageKey = Number.isInteger(field.pageIndex) ? field.pageIndex : 0;
            const current = maxOrderIndexByPage.get(pageKey) ?? -1;
            const nextValue = Math.max(current, Number(field.orderIndex) || 0);
            maxOrderIndexByPage.set(pageKey, nextValue);
        });

        const baseRects = new Map();
        baseSelection.forEach((field) => {
            const rect = normalizeField(field, pageSize);
            if (rect) baseRects.set(field.id, rect);
        });

        const sequenceSorted = baseSelection.slice().sort((a, b) => {
            const seqA = Number(a.sequenceNumber) || Number(a.orderIndex) || 0;
            const seqB = Number(b.sequenceNumber) || Number(b.orderIndex) || 0;
            if (seqA !== seqB) return seqA - seqB;
            return (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0);
        });

        const newFields = [];
        for (let rowIndex = 1; rowIndex < rowCount; rowIndex += 1) {
            sequenceSorted.forEach((field) => {
                const rect = baseRects.get(field.id);
                if (!rect) return;
                const nextY = clamp(rect.y + rowSpacing * rowIndex, 0, pageSize.height - rect.height);
                const nextOrderIndex = (maxOrderIndexByPage.get(field.pageIndex) ?? -1) + 1;
                maxOrderIndexByPage.set(field.pageIndex, nextOrderIndex);
                const shouldSkip = Boolean(field.skipSequence);
                const nextSequence = shouldSkip
                    ? Number.isInteger(field.sequenceNumber)
                        ? field.sequenceNumber
                        : 0
                    : (sequenceCursor += 1);
                newFields.push({
                    ...cloneState(field),
                    id: createId(),
                    xNorm: rect.x / pageSize.width,
                    yNorm: nextY / pageSize.height,
                    wNorm: rect.width / pageSize.width,
                    hNorm: rect.height / pageSize.height,
                    orderIndex: nextOrderIndex,
                    sequenceNumber: nextSequence,
                    [TABLE_ROW_GROUP_KEY]: groupId,
                    [TABLE_ROW_BASE_KEY]: field.id,
                    [TABLE_ROW_INDEX_KEY]: rowIndex,
                });
            });
        }

        if (newFields.length === 0) return;
        pushHistory();
        setFields((prev) =>
            prev
                .map((field) =>
                    baseIds.has(field.id)
                        ? {
                              ...field,
                              [TABLE_ROW_GROUP_KEY]: groupId,
                              [TABLE_ROW_BASE_KEY]: field.id,
                              [TABLE_ROW_INDEX_KEY]: 0,
                          }
                        : field,
                )
                .concat(newFields),
        );
        setSelectedIds((prev) => [
            ...prev,
            ...baseSelection.map((field) => field.id),
            ...newFields.map((field) => field.id),
        ]);
    }, [
        activeTableRowGroupId,
        currentPage,
        fields,
        hasActiveTableRowGroup,
        pageSize,
        pushHistory,
        selectedFields,
        tableRowConfig,
        updateTableRowGroup,
    ]);

    const handleTableRowConfigChange = useCallback(
        (key, value) => {
            setTableRowConfig((prev) => {
                const next = { ...prev, [key]: value };
                if (activeTool === 'tableRow' && hasActiveTableRowGroup) {
                    updateTableRowGroup(activeTableRowGroupId, next);
                }
                return next;
            });
        },
        [activeTool, activeTableRowGroupId, hasActiveTableRowGroup, updateTableRowGroup],
    );

    const matchSelectionSize = useCallback(
        (dimension) => {
            if (selectedRects.length < 2 || !pageSize.width || !pageSize.height) return;
            const reference =
                dimension === 'width' ? selectedRects[0].rect.width : selectedRects[0].rect.height;
            const updates = new Map();
            selectedRects.forEach(({ id, rect }) => {
                let x = rect.x;
                let y = rect.y;
                if (dimension === 'width') {
                    x = clamp(x, 0, pageSize.width - reference);
                }
                if (dimension === 'height') {
                    y = clamp(y, 0, pageSize.height - reference);
                }
                updates.set(id, {
                    ...rect,
                    x,
                    y,
                    width: dimension === 'width' ? reference : rect.width,
                    height: dimension === 'height' ? reference : rect.height,
                });
            });
            applySelectionRects(updates);
        },
        [applySelectionRects, pageSize, selectedRects],
    );

    const updateRectangleStyle = useCallback(
        (updates) => {
            if (selectedIds.length === 0) return;
            const baseStyle =
                selectedFields.length === 1 && selectedFields[0].type === 'rectangle'
                    ? normalizeRectStyle(selectedFields[0].styleJson)
                    : RECTANGLE_DEFAULT_STYLE;
            updateSelectedFields({ styleJson: { ...baseStyle, ...updates } });
        },
        [selectedFields, selectedIds, updateSelectedFields],
    );

    const handleImageUpload = useCallback(
        (file) => {
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    updateSelectedFields({ assetDataUrl: reader.result });
                }
            };
            reader.readAsDataURL(file);
        },
        [updateSelectedFields],
    );

    const updateSelectedRect = useCallback(
        (updates) => {
            if (selectedFields.length !== 1 || !pageSize.width || !pageSize.height) return;
            const field = selectedFields[0];
            const toPx = (value, size) =>
                Number.isFinite(Number(value)) ? Number(value) * size : null;
            const currentRect = {
                x: toPx(field.xNorm, pageSize.width),
                y: toPx(field.yNorm, pageSize.height),
                width: toPx(field.wNorm, pageSize.width),
                height: toPx(field.hNorm, pageSize.height),
            };
            const nextRect = { ...currentRect, ...updates };
            const nextUpdates = {};
            if (Object.prototype.hasOwnProperty.call(updates, 'x')) {
                nextUpdates.xNorm = nextRect.x === null ? null : nextRect.x / pageSize.width;
            }
            if (Object.prototype.hasOwnProperty.call(updates, 'y')) {
                nextUpdates.yNorm = nextRect.y === null ? null : nextRect.y / pageSize.height;
            }
            if (Object.prototype.hasOwnProperty.call(updates, 'width')) {
                nextUpdates.wNorm = nextRect.width === null ? null : nextRect.width / pageSize.width;
            }
            if (Object.prototype.hasOwnProperty.call(updates, 'height')) {
                nextUpdates.hNorm = nextRect.height === null ? null : nextRect.height / pageSize.height;
            }
            updateSelectedFields(nextUpdates);
        },
        [pageSize, selectedFields, updateSelectedFields],
    );

    const handleRectInputChange = useCallback(
        (key, rawValue) => {
            setRectInputs((prev) => ({ ...prev, [key]: rawValue }));
            if (rawValue === '') {
                updateSelectedRect({ [key]: null });
                return;
            }
            const numeric = Number(rawValue);
            if (!Number.isFinite(numeric)) {
                updateSelectedRect({ [key]: null });
                return;
            }
            updateSelectedRect({ [key]: numeric });
        },
        [updateSelectedRect],
    );

    const handleRectInputBlur = useCallback(
        (key, rawValue) => {
            if (rawValue === '') {
                return;
            }
            const numeric = Number(rawValue);
            if (!Number.isFinite(numeric)) {
                updateSelectedRect({ [key]: null });
                return;
            }
            updateSelectedRect({ [key]: numeric });
        },
        [updateSelectedRect],
    );

    const getFieldImage = useCallback((field) => {
        if (!field?.assetDataUrl) return null;
        const cached = imageCacheRef.current[field.id];
        if (cached?.src === field.assetDataUrl) {
            return cached.image;
        }
        const image = new window.Image();
        image.src = field.assetDataUrl;
        imageCacheRef.current[field.id] = { image, src: field.assetDataUrl };
        return image;
    }, []);

    const handleFontSizeChange = useCallback(
        (rawValue) => {
            const nextValue = Number(rawValue);
            if (!Number.isFinite(nextValue)) return;
            updateSelectedFields({ fontSize: nextValue });
        },
        [updateSelectedFields],
    );

    const sharedValue = (key) => {
        if (selectedFields.length === 0) return '';
        const first = selectedFields[0][key];
        const allSame = selectedFields.every((field) => field[key] === first);
        return allSame ? first : '';
    };

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const data = await fetchPdfTemplate(id);
                setTemplate(data);
                const incomingSections = (data.sections || []).length ? data.sections : [];
                const normalizedSections = incomingSections.map((section, index) => ({
                    id: section.id || createId(),
                    title: section.title || `שלב ${index + 1}`,
                    orderIndex: Number.isInteger(section.orderIndex) ? section.orderIndex : index,
                }));
                setSections(normalizedSections);
                setActiveSectionId(normalizedSections[0]?.id || null);

                const normalizedFields = (data.fields || []).map((field, index) => ({
                    ...field,
                    id: field.id || createId(),
                    orderIndex: Number.isInteger(field.orderIndex) ? field.orderIndex : index,
                    sequenceNumber: Number.isInteger(field.sequenceNumber) ? field.sequenceNumber : index + 1,
                    skipSequence: Boolean(field.skipSequence),
                    autoFill: field.autoFill || '',
                    suggestedValues: typeof field.suggestedValues === 'string' ? field.suggestedValues : '',
                    subtext: field.subtext || '',
                    fontSizeMode: field.fontSizeMode || 'auto_fit',
                    wrapMode: field.wrapMode || 'same_word',
                    rangeMode: field.rangeMode || 'single',
                    isMultiline: Boolean(field.isMultiline),
                    isReadOnly: Boolean(field.isReadOnly),
                    staticValue: field.staticValue || '',
                    assetDataUrl: typeof field.assetDataUrl === 'string' ? field.assetDataUrl : '',
                    optionsJson: normalizeOptions(field.optionsJson),
                    numericConfigJson: normalizeNumericConfig(field.numericConfigJson),
                    styleJson: field.type === 'rectangle' ? normalizeRectStyle(field.styleJson) : field.styleJson || null,
                }));
                setFields(normalizedFields);
                savedSnapshotRef.current = JSON.stringify({
                    fields: cloneState(normalizedFields),
                    sections: cloneState(normalizedSections),
                    activeSectionId: normalizedSections[0]?.id || null,
                });

                const pdf = await loadPdfDocument(data.fileUrl);
                setPdfDoc(pdf);
            } catch (err) {
                setError(err.response?.data?.message || TEXT.error);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [id]);

    useEffect(() => {
        let isMounted = true;
        fetchEditorPresets()
            .then((data) => {
                if (!isMounted) return;
                setPresets(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!isMounted) return;
                setPresets([]);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!pdfDoc || !containerRef.current) return;
        const observer = new ResizeObserver(async (entries) => {
            const width = entries[0]?.contentRect?.width || 0;
            if (!width) return;
            const size = await getPdfPageSize(pdfDoc, currentPage);
            setFitScale(width / size.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [pdfDoc, currentPage]);

    useEffect(() => {
        if (!pdfDoc || !fitScale) return;
        let isMounted = true;
        const render = async () => {
            const { canvas, viewport } = await renderPdfPage(pdfDoc, currentPage, fitScale);
            if (!isMounted) return;
            setPageCanvas(canvas);
            setPageSize({ width: viewport.width, height: viewport.height });
            const containerBox = containerRef.current?.getBoundingClientRect();
            const offsetX = containerBox ? containerBox.width - viewport.width : 0;
            const offsetY = containerBox ? containerBox.height - viewport.height : 0;
            const basePosition = {
                x: offsetX < 0 ? 0 : offsetX / 2,
                y: offsetY < 0 ? 0 : offsetY / 2,
            };
            setZoom(1);
            setStagePosition(basePosition);
        };
        render();
        return () => {
            isMounted = false;
        };
    }, [pdfDoc, currentPage, fitScale]);

    useEffect(() => {
        const transformer = transformerRef.current;
        if (!transformer) return;
        const selectedNodes = selectedIds
            .map((fieldId) => shapeRefs.current[fieldId])
            .filter(Boolean);
        transformer.nodes(selectedNodes.length === 1 ? selectedNodes : []);
        transformer.getLayer()?.batchDraw();
    }, [selectedIds, fields, currentPage]);

    const clampStagePosition = useCallback(
        (position, nextZoom = zoom) => {
            const container = containerRef.current;
            if (!container) return position;
            const { width, height } = container.getBoundingClientRect();
            const contentWidth = pageSize.width * nextZoom;
            const contentHeight = pageSize.height * nextZoom;
            const offsetX = width - contentWidth;
            const offsetY = height - contentHeight;
            const minX = offsetX < 0 ? offsetX : offsetX / 2;
            const maxX = offsetX < 0 ? 0 : offsetX / 2;
            const minY = offsetY < 0 ? offsetY : offsetY / 2;
            const maxY = offsetY < 0 ? 0 : offsetY / 2;
            return {
                x: clamp(position.x, minX, maxX),
                y: clamp(position.y, minY, maxY),
            };
        },
        [pageSize, zoom],
    );

    const applyZoom = useCallback(
        (nextZoom, pointer) => {
            if (!pageSize.width || !pageSize.height) return;
            const clampedZoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
            const anchor = pointer || { x: 0, y: 0 };
            const mousePoint = {
                x: (anchor.x - stagePosition.x) / zoom,
                y: (anchor.y - stagePosition.y) / zoom,
            };
            const newPos = {
                x: anchor.x - mousePoint.x * clampedZoom,
                y: anchor.y - mousePoint.y * clampedZoom,
            };
            setZoom(clampedZoom);
            setStagePosition(clampStagePosition(newPos, clampedZoom));
        },
        [clampStagePosition, pageSize, stagePosition, zoom],
    );

    const getCanvasCenter = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return { x: rect.width / 2, y: rect.height / 2 };
    }, []);

    const resolveCanvasPointer = useCallback((stage) => {
        const pointer = stage?.getPointerPosition?.();
        if (!pointer) return null;
        const transform = stage.getAbsoluteTransform().copy();
        transform.invert();
        const position = transform.point(pointer);
        return {
            x: clamp(position.x, 0, pageSize.width || position.x),
            y: clamp(position.y, 0, pageSize.height || position.y),
        };
    }, [pageSize.height, pageSize.width]);

    const getVisibleCanvasCenter = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || !zoom) {
            return {
                x: pageSize.width / 2,
                y: pageSize.height / 2,
            };
        }
        return {
            x: clamp((rect.width / 2 - stagePosition.x) / zoom, 0, pageSize.width),
            y: clamp((rect.height / 2 - stagePosition.y) / zoom, 0, pageSize.height),
        };
    }, [pageSize.height, pageSize.width, stagePosition.x, stagePosition.y, zoom]);

    const getPreferredInsertOrigin = useCallback(() => {
        const lastPointer = lastCanvasPointerRef.current;
        if (lastPointer && Number.isFinite(lastPointer.x) && Number.isFinite(lastPointer.y)) {
            return {
                x: clamp(lastPointer.x, 0, pageSize.width),
                y: clamp(lastPointer.y, 0, pageSize.height),
            };
        }
        return getVisibleCanvasCenter();
    }, [getVisibleCanvasCenter, pageSize.height, pageSize.width]);

    useEffect(() => {
        lastCanvasPointerRef.current = null;
    }, [currentPage]);

    useEffect(() => {
        setStagePosition((prev) => {
            const clamped = clampStagePosition(prev, zoom);
            if (clamped.x === prev.x && clamped.y === prev.y) {
                return prev;
            }
            return clamped;
        });
    }, [zoom, pageSize, clampStagePosition]);

    const handleSave = async () => {
        if (!template) return;
        try {
            setIsSaving(true);
            const payload = {
                sections: sections.map((section, index) => ({
                    id: section.id,
                    title: section.title,
                    orderIndex: section.orderIndex ?? index,
                })),
                fields: fields.map((field, index) => ({
                    ...field,
                    optionsJson: field.type === 'dropdown' ? normalizeOptions(field.optionsJson) : field.optionsJson,
                    numericConfigJson:
                        field.type === 'number' ? normalizeNumericConfig(field.numericConfigJson) : field.numericConfigJson,
                    orderIndex: Number.isInteger(field.orderIndex) ? field.orderIndex : index,
                })),
            };
            const updated = await savePdfTemplateSchema(template.id, payload);
            setTemplate(updated);
            setFields(updated.fields || []);
            setSections(updated.sections || []);
            savedSnapshotRef.current = JSON.stringify({
                fields: cloneState(updated.fields || []),
                sections: cloneState(updated.sections || []),
                activeSectionId,
            });
            setIsDirty(false);
        } catch (err) {
            setError(err.response?.data?.message || 'שגיאה בשמירה');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddSection = () => {
        pushHistory();
        const orderIndex = sections.length;
        const newSection = {
            id: createId(),
            title: `שלב ${orderIndex + 1}`,
            orderIndex,
        };
        setSections((prev) => [...prev, newSection]);
        setActiveSectionId(newSection.id);
    };

    const handleDeleteSection = (sectionId) => {
        if (sections.length <= 1) return;
        pushHistory();
        const remaining = sections.filter((section) => section.id !== sectionId);
        const fallbackId = remaining[0]?.id || null;
        setSections(remaining.map((section, index) => ({ ...section, orderIndex: index })));
        setFields((prev) =>
            prev.map((field) =>
                field.sectionId === sectionId ? { ...field, sectionId: fallbackId } : field,
            ),
        );
        setActiveSectionId((prev) => (prev === sectionId ? fallbackId : prev));
    };

    const handleAddField = (type, position) => {
        if (!pageSize.width || !pageSize.height) return;
        const size = DEFAULT_SIZES[type] || DEFAULT_SIZES.text;
        const x = clamp(position.x - size.width / 2, 0, pageSize.width - size.width);
        const y = clamp(position.y - size.height / 2, 0, pageSize.height - size.height);
        const nextSequence = fields.reduce(
            (maxValue, field) =>
                field.skipSequence ? maxValue : Math.max(maxValue, Number(field.sequenceNumber) || 0),
            0,
        ) + 1;
        const nextField = {
            id: createId(),
            type,
            label: FIELD_TYPE_LABELS[type] || type,
            key: '',
            pageIndex: currentPage,
            xNorm: x / pageSize.width,
            yNorm: y / pageSize.height,
            wNorm: size.width / pageSize.width,
            hNorm: size.height / pageSize.height,
            required: false,
            placeholder: '',
            fontSize: 12,
            align: '',
            direction: 'auto',
            autoFill: '',
            suggestedValues: type === 'text' ? '' : null,
            subtext: '',
            fontSizeMode: 'auto_fit',
            wrapMode: 'same_word',
            rangeMode: 'single',
            isMultiline: false,
            isReadOnly: false,
            staticValue: '',
            assetDataUrl: type === 'image' ? '' : null,
            optionsJson: type === 'dropdown' ? ['1', '2'] : [],
            numericConfigJson: type === 'number' ? { min: null, max: null, decimals: 0 } : {},
            styleJson: type === 'rectangle' ? { ...RECTANGLE_DEFAULT_STYLE } : null,
            sectionId: activeSectionId,
            orderIndex: fields.length,
            sequenceNumber: nextSequence,
            skipSequence: false,
        };
        pushHistory();
        setFields((prev) => [...prev, nextField]);
        setSelectedIds([nextField.id]);
        setActiveTool('select');
    };

    const handleDeleteSelected = useCallback(() => {
        if (selectedIds.length === 0) return;
        pushHistory();
        setFields((prev) => prev.filter((field) => !selectedIds.includes(field.id)));
        setSelectedIds([]);
    }, [selectedIds, pushHistory]);

    const writeSelectionClipboard = useCallback(async () => {
        if (selectedFields.length === 0) return null;
        const payload = buildSelectionClipboardPayload({
            fields: selectedFields,
            templateId: id,
        });
        return writeSelectionClipboardPayload(payload);
    }, [id, selectedFields]);

    const readClipboardPayload = useCallback(async () => {
        return readSelectionClipboardPayload();
    }, []);

    const insertClipboardPayload = useCallback(
        (payload) => {
            if (!payload?.fields?.length || !pageSize.width || !pageSize.height) return;
            const insertOriginPx = getPreferredInsertOrigin();
            const next = buildPastedFields({
                payload,
                existingFields: fields,
                pageSize,
                pageIndex: currentPage,
                activeSectionId,
                currentTemplateId: id,
                existingSectionIds: sections.map((section) => section.id),
                createId,
                insertOriginPx,
            });
            if (next.length === 0) return;
            pushHistory();
            setFields((prev) => [...prev, ...next]);
            setSelectedIds(next.map((field) => field.id));
        },
        [activeSectionId, currentPage, fields, getPreferredInsertOrigin, id, pageSize, pushHistory, sections],
    );

    const handleSavePreset = useCallback(async () => {
        const trimmedName = presetName.trim();
        if (!trimmedName || selectedFields.length === 0) return;
        try {
            setIsPresetSaving(true);
            setPresetError('');
            setPresetFeedback('');
            const payloadJson = buildPresetPayload({
                fields: selectedFields,
                pageSize,
            });
            const created = await createEditorPreset({
                name: trimmedName,
                payloadJson,
            });
            setPresets((prev) => [...prev, created]);
            setPresetName('');
            setPresetFeedback('האלמנט נשמר.');
        } catch (err) {
            setPresetError(err.response?.data?.message || 'לא ניתן לשמור את האלמנט.');
        } finally {
            setIsPresetSaving(false);
        }
    }, [pageSize, presetName, selectedFields]);

    const handleExportPresets = useCallback(() => {
        const payload = {
            type: 'laoreng-editor-presets',
            exportedAt: new Date().toISOString(),
            presets: presets
                .map((preset) => {
                    const parsedPayload = parsePresetPayloadValue(preset?.payloadJson);
                    if (!parsedPayload?.fields?.length) {
                        return null;
                    }
                    return {
                        name: String(preset?.name || '').trim() || 'אלמנט שמור',
                        payloadJson: parsedPayload,
                    };
                })
                .filter(Boolean),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        saveBlob(blob, `editor-presets-${new Date().toISOString().slice(0, 10)}.json`);
        setPresetError('');
        setPresetFeedback(TEXT.presetExportSuccess);
    }, [presets]);

    const handleImportPresets = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setPresetError('');
        setPresetFeedback('');
        try {
            setIsPresetImporting(true);
            const rawText = await file.text();
            const parsed = JSON.parse(rawText);
            const rawPresets = Array.isArray(parsed)
                ? parsed
                : Array.isArray(parsed?.presets)
                    ? parsed.presets
                    : [];
            const importablePresets = rawPresets
                .map((preset, index) => {
                    const payloadJson = parsePresetPayloadValue(preset?.payloadJson || preset?.payload);
                    const name = String(preset?.name || '').trim() || `אלמנט שמור ${index + 1}`;
                    if (!payloadJson?.fields?.length) {
                        return null;
                    }
                    return { name, payloadJson };
                })
                .filter(Boolean);

            if (importablePresets.length === 0) {
                setPresetError(TEXT.presetImportError);
                return;
            }

            const createdPresets = [];
            for (const preset of importablePresets) {
                const created = await createEditorPreset(preset);
                createdPresets.push(created);
            }

            setPresets((prev) => [...prev, ...createdPresets]);
            setPresetFeedback(TEXT.presetImportSuccess);
        } catch (err) {
            setPresetError(err.response?.data?.message || TEXT.presetImportError);
        } finally {
            setIsPresetImporting(false);
        }
    }, []);

    const handleInsertPreset = useCallback(
        (preset) => {
            if (!preset) return;
            let payload;
            try {
                payload = typeof preset.payloadJson === 'string'
                    ? JSON.parse(preset.payloadJson)
                    : preset.payloadJson;
            } catch {
                setPresetError('לא ניתן לטעון את האלמנט השמור.');
                return;
            }
            const next = buildFieldsFromPresetPayload({
                payload,
                existingFields: fields,
                pageSize,
                pageIndex: currentPage,
                activeSectionId,
                createId,
                insertOriginPx: getPreferredInsertOrigin(),
            });
            if (next.length === 0) return;
            pushHistory();
            setFields((prev) => [...prev, ...next]);
            setSelectedIds(next.map((field) => field.id));
        },
        [activeSectionId, currentPage, fields, getPreferredInsertOrigin, pageSize, pushHistory],
    );

    const handleDeletePreset = useCallback(async (presetId) => {
        try {
            await deleteEditorPreset(presetId);
            setPresets((prev) => prev.filter((preset) => preset.id !== presetId));
        } catch (err) {
            setPresetError(err.response?.data?.message || 'לא ניתן למחוק את האלמנט השמור.');
        }
    }, []);

    const handleUndo = useCallback(() => {
        setHistory((prev) => {
            if (prev.past.length === 0) return prev;
            const nextPast = [...prev.past];
            const snapshot = nextPast.pop();
            const future = [
                {
                    fields: cloneState(fields),
                    sections: cloneState(sections),
                    activeSectionId,
                    selectedIds: cloneState(selectedIds),
                },
                ...prev.future,
            ].slice(0, HISTORY_LIMIT);
            setFields(snapshot.fields || []);
            setSections(snapshot.sections || []);
            setActiveSectionId(snapshot.activeSectionId || snapshot.sections?.[0]?.id || null);
            setSelectedIds(snapshot.selectedIds || []);
            return { past: nextPast, future };
        });
    }, [fields, sections, activeSectionId, selectedIds]);

    const handleRedo = useCallback(() => {
        setHistory((prev) => {
            if (prev.future.length === 0) return prev;
            const [snapshot, ...rest] = prev.future;
            const past = [
                ...prev.past,
                {
                    fields: cloneState(fields),
                    sections: cloneState(sections),
                    activeSectionId,
                    selectedIds: cloneState(selectedIds),
                },
            ].slice(-HISTORY_LIMIT);
            setFields(snapshot.fields || []);
            setSections(snapshot.sections || []);
            setActiveSectionId(snapshot.activeSectionId || snapshot.sections?.[0]?.id || null);
            setSelectedIds(snapshot.selectedIds || []);
            return { past, future: rest };
        });
    }, [fields, sections, activeSectionId, selectedIds]);

    const handleKeyDown = useCallback(
        (event) => {
            const target = event.target;
            if (target && typeof target.closest === 'function' && target.closest('input, textarea, select')) {
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setSelectedIds([]);
                setSelectionRect((prev) => ({ ...prev, visible: false, width: 0, height: 0 }));
                return;
            }
            const isMeta = event.ctrlKey || event.metaKey;
            const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
            const code = event.code || '';
            if (isMeta && (key === 'z' || code === 'KeyZ')) {
                event.preventDefault();
                if (event.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }
                return;
            }
            if (isMeta && (key === 'y' || code === 'KeyY')) {
                event.preventDefault();
                handleRedo();
                return;
            }
            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                handleDeleteSelected();
                return;
            }
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                if (selectedIds.length === 0) return;
                event.preventDefault();
                const step = event.shiftKey ? 10 : 1;
                const delta = {
                    x: event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0,
                    y: event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0,
                };
                pushHistory();
                setFields((prev) =>
                    prev.map((field) => {
                        if (!selectedIds.includes(field.id) || field.pageIndex !== currentPage) {
                            return field;
                        }
                        const rect = normalizeField(field, pageSize);
                        const nextX = clamp(rect.x + delta.x, 0, pageSize.width - rect.width);
                        const nextY = clamp(rect.y + delta.y, 0, pageSize.height - rect.height);
                        return {
                            ...field,
                            xNorm: nextX / pageSize.width,
                            yNorm: nextY / pageSize.height,
                        };
                    }),
                );
                return;
            }
            if (isMeta && (key === 'c' || code === 'KeyC')) {
                if (selectedFields.length === 0) return;
                event.preventDefault();
                void writeSelectionClipboard();
                return;
            }
            if (isMeta && (key === 'x' || code === 'KeyX')) {
                if (selectedFields.length === 0) return;
                event.preventDefault();
                void writeSelectionClipboard().then(() => {
                    handleDeleteSelected();
                });
                return;
            }
            if (isMeta && (key === 'v' || code === 'KeyV')) {
                event.preventDefault();
                void readClipboardPayload().then((clipboard) => {
                    insertClipboardPayload(clipboard);
                });
            }
        },
        [
            handleDeleteSelected,
            handleRedo,
            handleUndo,
            insertClipboardPayload,
            pageSize,
            pushHistory,
            readClipboardPayload,
            selectedFields,
            selectedIds,
            currentPage,
            writeSelectionClipboard,
        ],
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const handleStageClick = (event) => {
        const stage = event.target.getStage();
        const isEmpty = event.target === stage;
        if (!isEmpty) {
            return;
        }
        const position = resolveCanvasPointer(stage);
        if (!position) return;
        lastCanvasPointerRef.current = position;
        if (!isSelectTool) {
            handleAddField(activeTool, position);
            return;
        }
        const nativeEvent = resolveNativeEvent(event);
        const isMulti = Boolean(nativeEvent?.shiftKey || nativeEvent?.ctrlKey || nativeEvent?.metaKey);
        if (!isMulti) {
            setSelectedIds([]);
        }
        setSelectionRect({
            x: position.x,
            y: position.y,
            width: 0,
            height: 0,
            visible: true,
            additive: isMulti,
        });
    };

    const handleStageMouseMove = (event) => {
        const stage = event.target.getStage?.();
        if (stage) {
            const position = resolveCanvasPointer(stage);
            if (position) {
                lastCanvasPointerRef.current = position;
            }
        }
        if (selectionRect.visible) {
            const pos = lastCanvasPointerRef.current;
            if (!pos) return;
            const newRect = {
                x: Math.min(selectionRect.x, pos.x),
                y: Math.min(selectionRect.y, pos.y),
                width: Math.abs(selectionRect.x - pos.x),
                height: Math.abs(selectionRect.y - pos.y),
                visible: true,
            };
            setSelectionRect(newRect);
            return;
        }
        if (!panState.current.isPanning) return;
        const nativeEvent = resolveNativeEvent(event);
        const clientPoint = resolveClientPoint(nativeEvent);
        if (!clientPoint) return;
        const nextPosition = {
            x: panState.current.origin.x + (clientPoint.x - panState.current.startX),
            y: panState.current.origin.y + (clientPoint.y - panState.current.startY),
        };
        setStagePosition(clampStagePosition(nextPosition, zoom));
    };

    const handleStageMouseUp = () => {
        if (selectionRect.visible) {
            const selection = selectionRect;
            const isClickSelection =
                selection.width <= SELECTION_CLICK_THRESHOLD && selection.height <= SELECTION_CLICK_THRESHOLD;
            if (isClickSelection) {
                setSelectedIds([]);
            } else {
                const hits = pageFields
                    .filter((field) => {
                        const rect = normalizeField(field, pageSize);
                        const intersects =
                            rect.x < selection.x + selection.width &&
                            rect.x + rect.width > selection.x &&
                            rect.y < selection.y + selection.height &&
                            rect.y + rect.height > selection.y;
                        return intersects;
                    })
                    .map((field) => field.id);
                if (selection.additive) {
                    setSelectedIds((prev) => Array.from(new Set([...prev, ...hits])));
                } else {
                    setSelectedIds(hits);
                }
            }
            setSelectionRect((prev) => ({ ...prev, visible: false, width: 0, height: 0 }));
        }
        panState.current.isPanning = false;
    };

    const handleWheel = (event) => {
        const nativeEvent = resolveNativeEvent(event);
        nativeEvent?.preventDefault?.();
        const stage = stageRef.current;
        if (!stage) return;
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        const scaleBy = 1.05;
        const direction = (nativeEvent?.deltaY ?? 0) > 0 ? -1 : 1;
        const newScale = zoom * (direction > 0 ? scaleBy : 1 / scaleBy);
        applyZoom(newScale, pointer);
    };

    const handlePointerDown = (event) => {
        const nativeEvent = resolveNativeEvent(event);
        if (!nativeEvent?.pointerId) return;
        const pointers = pointerState.current.pointers;
        pointers.set(nativeEvent.pointerId, { x: nativeEvent.clientX, y: nativeEvent.clientY });
        if (pointers.size === 2) {
            const [first, second] = Array.from(pointers.values());
            pointerState.current.lastCenter = {
                x: (first.x + second.x) / 2,
                y: (first.y + second.y) / 2,
            };
            pointerState.current.lastDistance = Math.hypot(first.x - second.x, first.y - second.y);
        }
    };

    const handlePointerMove = (event) => {
        const stage = stageRef.current;
        const nativeEvent = resolveNativeEvent(event);
        if (!stage || !nativeEvent?.pointerId) return;
        const pointers = pointerState.current.pointers;
        if (!pointers.has(nativeEvent.pointerId)) return;
        pointers.set(nativeEvent.pointerId, { x: nativeEvent.clientX, y: nativeEvent.clientY });
        if (pointers.size < 2) return;
        const [first, second] = Array.from(pointers.values());
        const newCenter = {
            x: (first.x + second.x) / 2,
            y: (first.y + second.y) / 2,
        };
        const newDistance = Math.hypot(first.x - second.x, first.y - second.y);
        if (!pointerState.current.lastCenter) return;
        const scaleChange = newDistance / pointerState.current.lastDistance;
        const newScale = zoom * scaleChange;
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return;
        const pointer = {
            x: newCenter.x - containerRect.left,
            y: newCenter.y - containerRect.top,
        };
        applyZoom(newScale, pointer);
        pointerState.current.lastCenter = newCenter;
        pointerState.current.lastDistance = newDistance;
    };

    const handlePointerUp = (event) => {
        const nativeEvent = resolveNativeEvent(event);
        if (!nativeEvent?.pointerId) return;
        const pointers = pointerState.current.pointers;
        pointers.delete(nativeEvent.pointerId);
        if (pointers.size < 2) {
            pointerState.current.lastCenter = null;
            pointerState.current.lastDistance = 0;
        }
    };

    const handleZoomIn = () => {
        applyZoom(zoom * 1.1, getCanvasCenter());
    };

    const handleZoomOut = () => {
        applyZoom(zoom / 1.1, getCanvasCenter());
    };

    const handleFitWidth = () => {
        applyZoom(1, getCanvasCenter());
    };

    const handleFullSize = () => {
        if (!fitScale) return;
        applyZoom(1 / fitScale, getCanvasCenter());
    };

    const buildExportPayload = useCallback(async () => {
        const pageCount = pdfDoc?.numPages || 0;
        const pageSizes = [];
        if (pdfDoc && pageCount > 0) {
            for (let index = 0; index < pageCount; index += 1) {
                try {
                    const size = await getPdfPageSize(pdfDoc, index);
                    if (size && Number.isFinite(size.width) && Number.isFinite(size.height)) {
                        pageSizes[index] = { width: size.width, height: size.height };
                    } else {
                        pageSizes[index] = null;
                    }
                } catch {
                    pageSizes[index] = null;
                }
            }
        }
        const payloadFields = fields.map((field) => {
            const pageIndex = Number.isFinite(Number(field.pageIndex)) ? Number(field.pageIndex) : 0;
            const size = pageSizes[pageIndex];
            if (!size || !Number.isFinite(size.width) || !Number.isFinite(size.height)) {
                return cloneState(field);
            }
            const xNorm = Number(field.xNorm);
            const yNorm = Number(field.yNorm);
            const wNorm = Number(field.wNorm);
            const hNorm = Number(field.hNorm);
            if (![xNorm, yNorm, wNorm, hNorm].every(Number.isFinite)) {
                return cloneState(field);
            }
            return {
                ...cloneState(field),
                xPx: xNorm * size.width,
                yPx: yNorm * size.height,
                wPx: wNorm * size.width,
                hPx: hNorm * size.height,
            };
        });
        return JSON.stringify({ sections, fields: payloadFields, pageSizes }, null, 2);
    }, [fields, pdfDoc, sections]);

    const handleCopyExport = useCallback(async () => {
        if (!exportPayload) return;
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(exportPayload);
                setExportFeedback(TEXT.exportCopied);
                setIsExportOpen(false);
                return;
            } catch {
                // ignore copy failures
            }
        }
    }, [exportPayload]);

    const handleExportSchema = useCallback(async () => {
        let payload;
        try {
            payload = await buildExportPayload();
        } catch {
            payload = JSON.stringify({ sections, fields }, null, 2);
        }
        setExportPayload(payload);
        setExportFeedback('');
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(payload);
                setExportFeedback(TEXT.exportCopied);
                return;
            } catch {
                // fallback to modal
            }
        }
        setIsExportOpen(true);
    }, [buildExportPayload, fields, sections]);

    const handleOpenImport = useCallback(() => {
        setImportPayload('');
        setImportError('');
        setImportFeedback('');
        setIsImportOpen(true);
    }, []);

    const handleImportSchema = useCallback(async () => {
        setImportError('');
        setExportFeedback('');
        let parsed;
        try {
            parsed = JSON.parse(importPayload || '');
        } catch {
            setImportError(TEXT.importError);
            return;
        }
        const incomingFields = Array.isArray(parsed?.fields) ? parsed.fields : [];
        if (incomingFields.length === 0) {
            setImportError(TEXT.importError);
            return;
        }
        const incomingSections = Array.isArray(parsed?.sections) ? parsed.sections : [];
        const baseSections = sections || [];
        const baseFields = fields || [];
        const maxPageIndex = Math.max(0, (pdfDoc?.numPages || 1) - 1);
        const rawPageSizes = parsed?.pageSizes;
        const sourcePageSizes = Array.isArray(rawPageSizes)
            ? rawPageSizes
            : rawPageSizes && typeof rawPageSizes === 'object'
                ? Object.keys(rawPageSizes).reduce((acc, key) => {
                      const index = Number(key);
                      if (Number.isFinite(index)) {
                          acc[index] = rawPageSizes[key];
                      }
                      return acc;
                  }, [])
                : [];
        const targetPageSizes = [];
        if (pdfDoc) {
            const targetCount = pdfDoc.numPages || 1;
            for (let index = 0; index < targetCount; index += 1) {
                try {
                    const size = await getPdfPageSize(pdfDoc, index);
                    if (size && Number.isFinite(size.width) && Number.isFinite(size.height)) {
                        targetPageSizes[index] = { width: size.width, height: size.height };
                    } else {
                        targetPageSizes[index] = null;
                    }
                } catch {
                    targetPageSizes[index] = null;
                }
            }
        }
        const fallbackTargetSize =
            pageSize &&
            Number.isFinite(pageSize.width) &&
            Number.isFinite(pageSize.height) &&
            pageSize.width > 0 &&
            pageSize.height > 0
                ? { width: pageSize.width, height: pageSize.height }
                : null;
        const resolvePageSize = (sizes, index) => {
            const size = sizes?.[index];
            if (!size) return null;
            const width = Number(size.width);
            const height = Number(size.height);
            if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
                return null;
            }
            return { width, height };
        };
        const toFinite = (value, fallback) => {
            const parsedValue = Number(value);
            return Number.isFinite(parsedValue) ? parsedValue : fallback;
        };
        const clampNormValue = (value) => (Number.isFinite(value) ? clamp(value, 0, 1) : value);

        const sectionIdMap = new Map();
        const sectionOrderStart = baseSections.length;
        const importedSections = incomingSections.map((section, index) => {
            const newId = createId();
            const rawTitle = typeof section?.title === 'string' ? section.title.trim() : '';
            const title = rawTitle || `שלב ${sectionOrderStart + index + 1}`;
            sectionIdMap.set(String(section?.id || `import-section-${index}`), newId);
            return {
                ...cloneState(section),
                id: newId,
                title,
                orderIndex: sectionOrderStart + index,
            };
        });
        const allSections = baseSections.concat(importedSections);
        const fallbackSectionId = activeSectionId || importedSections[0]?.id || allSections[0]?.id || null;

        const entryList = incomingFields.map((field, index) => ({
            field,
            key: String(field?.id || `import-${index}`),
        }));
        const fieldIdMap = new Map();
        entryList.forEach(({ key }) => {
            fieldIdMap.set(key, createId());
        });
        const groupIdMap = new Map();
        entryList.forEach(({ field }) => {
            const groupId = field?.[TABLE_ROW_GROUP_KEY];
            if (!groupId) return;
            const key = String(groupId);
            if (!groupIdMap.has(key)) {
                groupIdMap.set(key, createId());
            }
        });

        const maxOrderIndexByPage = new Map();
        baseFields.forEach((field) => {
            const pageKey = Number.isInteger(field.pageIndex) ? field.pageIndex : 0;
            const current = maxOrderIndexByPage.get(pageKey) ?? -1;
            maxOrderIndexByPage.set(pageKey, Math.max(current, Number(field.orderIndex) || 0));
        });
        let sequenceCursor = baseFields.reduce(
            (maxValue, field) =>
                field.skipSequence
                    ? maxValue
                    : Math.max(maxValue, Number(field.sequenceNumber) || 0),
            0,
        );
        const sequenceSorted = entryList
            .slice()
            .sort((a, b) => {
                const seqA = Number(a.field?.sequenceNumber) || Number(a.field?.orderIndex) || 0;
                const seqB = Number(b.field?.sequenceNumber) || Number(b.field?.orderIndex) || 0;
                if (seqA !== seqB) return seqA - seqB;
                return a.key.localeCompare(b.key);
            });
        const sequenceMap = new Map();
        sequenceSorted.forEach(({ field, key }) => {
            if (field?.skipSequence) {
                const rawSeq = Number(field?.sequenceNumber);
                sequenceMap.set(key, Number.isFinite(rawSeq) ? rawSeq : 0);
                return;
            }
            sequenceCursor += 1;
            sequenceMap.set(key, sequenceCursor);
        });

        const importedFields = entryList.map(({ field, key }) => {
            const newId = fieldIdMap.get(key);
            const rawPageIndex = Number(field?.pageIndex);
            const pageIndex = Number.isFinite(rawPageIndex)
                ? Math.min(Math.max(rawPageIndex, 0), maxPageIndex)
                : 0;
            const nextOrder = (maxOrderIndexByPage.get(pageIndex) ?? -1) + 1;
            maxOrderIndexByPage.set(pageIndex, nextOrder);
            const sectionKey = field?.sectionId ? String(field.sectionId) : '';
            const mappedSectionId = sectionKey ? sectionIdMap.get(sectionKey) : null;
            const clonedField = cloneState(field);
            const { xPx, yPx, wPx, hPx, ...rest } = clonedField || {};
            const fallbackX = toFinite(rest.xNorm, 0);
            const fallbackY = toFinite(rest.yNorm, 0);
            const fallbackW = toFinite(rest.wNorm, 0.1);
            const fallbackH = toFinite(rest.hNorm, 0.05);
            let nextXNorm = fallbackX;
            let nextYNorm = fallbackY;
            let nextWNorm = fallbackW;
            let nextHNorm = fallbackH;
            const sourceSize =
                resolvePageSize(sourcePageSizes, pageIndex) ||
                resolvePageSize(sourcePageSizes, 0);
            const targetSize =
                resolvePageSize(targetPageSizes, pageIndex) ||
                resolvePageSize(targetPageSizes, 0) ||
                fallbackTargetSize;
            if (sourceSize && targetSize) {
                const sourceWidth = sourceSize.width;
                const sourceHeight = sourceSize.height;
                const targetWidth = targetSize.width;
                const targetHeight = targetSize.height;
                const resolvedXPx = Number.isFinite(Number(xPx))
                    ? Number(xPx)
                    : Number.isFinite(fallbackX)
                        ? fallbackX * sourceWidth
                        : null;
                const resolvedYPx = Number.isFinite(Number(yPx))
                    ? Number(yPx)
                    : Number.isFinite(fallbackY)
                        ? fallbackY * sourceHeight
                        : null;
                const resolvedWPx = Number.isFinite(Number(wPx))
                    ? Number(wPx)
                    : Number.isFinite(fallbackW)
                        ? fallbackW * sourceWidth
                        : null;
                const resolvedHPx = Number.isFinite(Number(hPx))
                    ? Number(hPx)
                    : Number.isFinite(fallbackH)
                        ? fallbackH * sourceHeight
                        : null;
                if (Number.isFinite(resolvedXPx) && targetWidth > 0) {
                    nextXNorm = resolvedXPx / targetWidth;
                }
                if (Number.isFinite(resolvedYPx) && targetHeight > 0) {
                    nextYNorm = resolvedYPx / targetHeight;
                }
                if (Number.isFinite(resolvedWPx) && targetWidth > 0) {
                    nextWNorm = resolvedWPx / targetWidth;
                }
                if (Number.isFinite(resolvedHPx) && targetHeight > 0) {
                    nextHNorm = resolvedHPx / targetHeight;
                }
            }
            nextXNorm = clampNormValue(nextXNorm);
            nextYNorm = clampNormValue(nextYNorm);
            nextWNorm = clampNormValue(nextWNorm);
            nextHNorm = clampNormValue(nextHNorm);
            const nextField = {
                ...rest,
                id: newId,
                sectionId: mappedSectionId || fallbackSectionId,
                pageIndex,
                orderIndex: nextOrder,
                sequenceNumber: sequenceMap.get(key) ?? 0,
                xNorm: Number.isFinite(nextXNorm) ? nextXNorm : fallbackX,
                yNorm: Number.isFinite(nextYNorm) ? nextYNorm : fallbackY,
                wNorm: Number.isFinite(nextWNorm) ? nextWNorm : fallbackW,
                hNorm: Number.isFinite(nextHNorm) ? nextHNorm : fallbackH,
            };
            if (nextField[TABLE_ROW_GROUP_KEY]) {
                const groupKey = String(nextField[TABLE_ROW_GROUP_KEY]);
                nextField[TABLE_ROW_GROUP_KEY] = groupIdMap.get(groupKey);
            }
            if (nextField[TABLE_ROW_BASE_KEY]) {
                const baseKey = String(nextField[TABLE_ROW_BASE_KEY]);
                nextField[TABLE_ROW_BASE_KEY] = fieldIdMap.get(baseKey) || newId;
            }
            if (nextField[TABLE_ROW_GROUP_KEY] && nextField[TABLE_ROW_INDEX_KEY] === undefined) {
                nextField[TABLE_ROW_INDEX_KEY] = 0;
            }
            return nextField;
        });

        pushHistory();
        setSections(allSections);
        setFields(baseFields.concat(importedFields));
        setSelectedIds(importedFields.map((field) => field.id));
        if (importedSections.length > 0) {
            setActiveSectionId(importedSections[0].id);
        }
        setImportFeedback(TEXT.importSuccess);
        setIsImportOpen(false);
    }, [
        activeSectionId,
        fields,
        importPayload,
        pageSize,
        pdfDoc,
        pushHistory,
        sections,
    ]);

    const handleFieldSelect = (field, event) => {
        if (!field) return;
        const nativeEvent = resolveNativeEvent(event);
        const isMulti = Boolean(nativeEvent?.shiftKey || nativeEvent?.ctrlKey || nativeEvent?.metaKey);
        if (isMulti) {
            setSelectedIds((prev) =>
                prev.includes(field.id) ? prev.filter((idValue) => idValue !== field.id) : [...prev, field.id],
            );
        } else {
            setSelectedIds([field.id]);
        }
        if (field.sectionId) {
            setActiveSectionId(field.sectionId);
        }
    };

    const handleDragStart = (field, event) => {
        if (selectedIds.length <= 1 || !selectionBounds || selectedRects.length === 0) {
            return;
        }
        const node = event.target;
        dragGroupRef.current = {
            fieldId: field.id,
            startX: node.x(),
            startY: node.y(),
            rects: selectedRects.map((entry) => ({ id: entry.id, rect: { ...entry.rect } })),
            bounds: selectionBounds,
        };
        pushHistory();
    };

    const handleDragMove = (field, event) => {
        if (selectedIds.length <= 1 || !dragGroupRef.current || !pageSize.width || !pageSize.height) {
            return;
        }
        const dragState = dragGroupRef.current;
        const node = event.target;
        const rawX = snapEnabled ? snapValue(node.x()) : node.x();
        const rawY = snapEnabled ? snapValue(node.y()) : node.y();
        let dx = rawX - dragState.startX;
        let dy = rawY - dragState.startY;
        if (dragState.bounds) {
            const minDx = -dragState.bounds.minX;
            const maxDx = pageSize.width - dragState.bounds.maxX;
            const minDy = -dragState.bounds.minY;
            const maxDy = pageSize.height - dragState.bounds.maxY;
            dx = clamp(dx, minDx, maxDx);
            dy = clamp(dy, minDy, maxDy);
        }
        const updates = new Map();
        dragState.rects.forEach(({ id, rect }) => {
            updates.set(id, { ...rect, x: rect.x + dx, y: rect.y + dy });
        });
        setFields((prev) =>
            prev.map((item) => {
                const nextRect = updates.get(item.id);
                if (!nextRect) return item;
                return {
                    ...item,
                    xNorm: nextRect.x / pageSize.width,
                    yNorm: nextRect.y / pageSize.height,
                };
            }),
        );
    };

    const handleDragEnd = (field, event) => {
        if (selectedIds.length > 1 && dragGroupRef.current) {
            dragGroupRef.current = null;
            return;
        }
        const node = event.target;
        const width = field.wNorm * pageSize.width;
        const height = field.hNorm * pageSize.height;
        const nextX = snapEnabled ? snapValue(node.x()) : node.x();
        const nextY = snapEnabled ? snapValue(node.y()) : node.y();
        const clampedX = clamp(nextX, 0, pageSize.width - width);
        const clampedY = clamp(nextY, 0, pageSize.height - height);
        pushHistory();
        setFields((prev) =>
            prev.map((item) =>
                item.id === field.id
                    ? {
                          ...item,
                          xNorm: clampedX / pageSize.width,
                          yNorm: clampedY / pageSize.height,
                      }
                    : item,
            ),
        );
    };

    const handleTransformEnd = (field, event) => {
        const node = event.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);
        let width = node.width() * scaleX;
        let height = node.height() * scaleY;
        if (snapEnabled) {
            width = snapValue(width);
            height = snapValue(height);
        }
        let x = node.x();
        let y = node.y();
        x = clamp(x, 0, pageSize.width - width);
        y = clamp(y, 0, pageSize.height - height);
        pushHistory();
        setFields((prev) =>
            prev.map((item) =>
                item.id === field.id
                    ? {
                          ...item,
                          xNorm: x / pageSize.width,
                          yNorm: y / pageSize.height,
                          wNorm: width / pageSize.width,
                          hNorm: height / pageSize.height,
                      }
                    : item,
            ),
        );
    };

    if (isLoading) {
        return (
            <div className="page stack">
                <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                    {TEXT.back}
                </button>
                <div className="card">{TEXT.loading}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page stack">
                <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                    {TEXT.back}
                </button>
                <div className="alert alert--danger">{error}</div>
            </div>
        );
    }

    return (
        <div className="pdf-builder">
            <header className="pdf-builder__header">
                <div className="pdf-builder__title">
                    <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                        {TEXT.back}
                    </button>
                    <div className="stack">
                        <h1 className="page__title">{template?.name}</h1>
                        {template?.category && <span className="card__meta">{template.category}</span>}
                    </div>
                </div>
                <div className="pdf-builder__actions">
                    <button type="button" className="button button--light" onClick={handleUndo}>
                        <span className="button__icon" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M7 7H3v4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M3 11a8 8 0 1 0 2.3-5.7L3 7"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        {TEXT.undo}
                    </button>
                    <button type="button" className="button button--light" onClick={handleRedo}>
                        <span className="button__icon" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M17 7h4v4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M21 11a8 8 0 1 1-2.3-5.7L21 7"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        {TEXT.redo}
                    </button>
                    <button type="button" className="button button--light" onClick={handleExportSchema}>
                        {TEXT.exportElements}
                    </button>
                    <button type="button" className="button button--light" onClick={handleOpenImport}>
                        {TEXT.importElements}
                    </button>
                    <button
                        type="button"
                        className="button button--light"
                        onClick={() => navigate(`/admin/pdf-templates/${id}/preview`)}
                    >
                        {TEXT.preview}
                    </button>
                    <button
                        type="button"
                        className="button button--primary"

                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? TEXT.saving : TEXT.save}
                    </button>
                </div>
            </header>

            {exportFeedback && <div className="alert alert--success">{exportFeedback}</div>}
            {importFeedback && <div className="alert alert--success">{importFeedback}</div>}

            <div
                className={`pdf-builder__body${isLeftCollapsed ? ' is-left-collapsed' : ''}${isRightCollapsed ? ' is-right-collapsed' : ''}`}
            >
                <aside className={`pdf-builder__sidebar${isLeftCollapsed ? ' is-collapsed' : ''}`}>
                    <div className="pdf-builder__collapse">
                        <button
                            type="button"
                            className="icon-button icon-button--compact"
                            onClick={() => setIsLeftCollapsed((prev) => !prev)}
                            title={isLeftCollapsed ? TEXT.expandTools : TEXT.collapseTools}
                        >
                            <span className="button__icon" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M9 6l6 6-6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                            <span className="sr-only">
                                {isLeftCollapsed ? TEXT.expandTools : TEXT.collapseTools}
                            </span>
                        </button>
                    </div>
                    {!isLeftCollapsed && (
                        <>
                            <div className="pdf-builder__panel">
                                <div className="pdf-builder__tab-row">
                                    <button
                                        type="button"
                                        className={`tool-button${activeSidebarTab === 'tools' ? ' active' : ''}`}
                                        onClick={() => setActiveSidebarTab('tools')}
                                    >
                                        {TEXT.tools}
                                    </button>
                                    <button
                                        type="button"
                                        className={`tool-button${activeSidebarTab === 'presets' ? ' active' : ''}`}
                                        onClick={() => setActiveSidebarTab('presets')}
                                    >
                                        {TEXT.presets}
                                    </button>
                                </div>
                            </div>

                            {activeSidebarTab === 'tools' ? (
                                <>
                                    <div className="pdf-builder__panel">
                                        <h3>{TEXT.tools}</h3>
                                        <div className="tool-grid">
                                            <button
                                                type="button"
                                                className={`tool-button${activeTool === 'select' ? ' active' : ''}`}
                                                onClick={() => setActiveTool('select')}
                                            >
                                                {TEXT.select}
                                            </button>
                                            <button
                                                type="button"
                                                className={`tool-button${activeTool === 'tableRow' ? ' active' : ''}`}
                                                onClick={() => setActiveTool('tableRow')}
                                            >
                                                {TEXT.tableRow}
                                            </button>
                                            {FIELD_TYPES.map((tool) => (
                                                <button
                                                    key={tool.type}
                                                    type="button"
                                                    className={`tool-button${activeTool === tool.type ? ' active' : ''}`}
                                                    onClick={() => setActiveTool(tool.type)}
                                                >
                                                    {tool.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="pdf-builder__panel">
                                        <div className="pdf-builder__panel-header">
                                            <h3>{TEXT.sections}</h3>
                                            <button
                                                type="button"
                                                className="icon-button icon-button--compact"
                                                onClick={handleAddSection}
                                                title={TEXT.addSection}
                                            >
                                                +
                                            </button>
                                        </div>
                                        <div className="section-list">
                                            {sections.map((section) => (
                                                <div
                                                    key={section.id}
                                                    className={`section-row${activeSectionId === section.id ? ' active' : ''}`}
                                                >
                                                    <input
                                                        className="form-field__control"
                                                        value={section.title}
                                                        onChange={(event) => {
                                                            const title = event.target.value;
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item.id === section.id ? { ...item, title } : item,
                                                                ),
                                                            );
                                                        }}
                                                    />
                                                    <div className="section-row__actions">
                                                        <button
                                                            type="button"
                                                            className="icon-button icon-button--compact"
                                                            onClick={() => setActiveSectionId(section.id)}
                                                            title={TEXT.activeSection}
                                                        >
                                                            {activeSectionId === section.id ? '*' : 'o'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="icon-button icon-button--compact icon-button--danger"
                                                            onClick={() => handleDeleteSection(section.id)}
                                                            disabled={sections.length <= 1}
                                                            title={TEXT.deleteSection}
                                                        >
                                                            <span className="button__icon" aria-hidden="true">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                    <path
                                                                        d="M4 7h16M9 7V5h6v2m-7 4v7m4-7v7m4-7v7"
                                                                        stroke="currentColor"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </span>
                                                            <span className="sr-only">{TEXT.deleteSection}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="pdf-builder__panel stack">
                                    <h3>{TEXT.savedElements}</h3>
                                    <div className="flex flex--wrap">
                                        <button
                                            type="button"
                                            className="button button--light"
                                            onClick={handleExportPresets}
                                            disabled={presets.length === 0}
                                        >
                                            {TEXT.exportPresets}
                                        </button>
                                        <button
                                            type="button"
                                            className="button button--light"
                                            onClick={() => presetImportInputRef.current?.click()}
                                            disabled={isPresetImporting}
                                        >
                                            {TEXT.importPresets}
                                        </button>
                                        <input
                                            ref={presetImportInputRef}
                                            type="file"
                                            accept="application/json,.json"
                                            onChange={handleImportPresets}
                                            hidden
                                        />
                                    </div>
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.saveCurrentSelection}</span>
                                        <input
                                            className="form-field__control"
                                            type="text"
                                            value={presetName}
                                            onChange={(event) => setPresetName(event.target.value)}
                                            placeholder={TEXT.presetName}
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className="button button--primary"
                                        onClick={handleSavePreset}
                                        disabled={isPresetSaving || selectedFields.length === 0}
                                    >
                                        {isPresetSaving ? TEXT.savingPreset : TEXT.savePreset}
                                    </button>
                                    {presetError && <div className="alert alert--danger">{presetError}</div>}
                                    {presetFeedback && <div className="alert alert--success">{presetFeedback}</div>}
                                    <div className="stack">
                                        {presets.length === 0 ? (
                                            <div className="card__meta">{TEXT.noSavedElements}</div>
                                        ) : (
                                            presets.map((preset) => (
                                                <div key={preset.id} className="section-row">
                                                    <div className="stack stack--sm">
                                                        <strong>{preset.name}</strong>
                                                        <span className="card__meta">
                                                            {(parsePresetPayloadValue(preset.payloadJson)?.fields || []).length} {TEXT.presetFields}
                                                        </span>
                                                    </div>
                                                    <div className="section-row__actions">
                                                        <button
                                                            type="button"
                                                            className="button button--light"
                                                            onClick={() => handleInsertPreset(preset)}
                                                        >
                                                            {TEXT.insertPreset}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button button--danger"
                                                            onClick={() => handleDeletePreset(preset.id)}
                                                        >
                                                            {TEXT.deletePreset}
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </aside>

                <main className="pdf-builder__canvas">
                    <div className="pdf-builder__toolbar">
                        <div className="pdf-builder__toolbar-group">
                            <button
                                type="button"
                                className="icon-button icon-button--compact"
                                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                                disabled={currentPage === 0}
                                title={`${TEXT.page} ${TEXT.prevPage}`}
                            >
                                <span className="button__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M15 18l-6-6 6-6"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                            <span className="pdf-builder__toolbar-label">
                                {TEXT.page} {currentPage + 1} {TEXT.pageOf} {pageCount}
                            </span>
                            <button
                                type="button"
                                className="icon-button icon-button--compact"
                                onClick={() => setCurrentPage((prev) => Math.min(pageCount - 1, prev + 1))}
                                disabled={currentPage >= pageCount - 1}
                                title={`${TEXT.page} ${TEXT.nextPage}`}
                            >
                                <span className="button__icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M9 6l6 6-6 6"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                        </div>
                        <div className="pdf-builder__toolbar-group">
                            <button
                                type="button"
                                className="icon-button icon-button--compact"
                                onClick={handleZoomOut}
                                title={TEXT.zoomOut}
                            >
                                <span className="button__icon" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M6 12h12"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                            <span className="pdf-builder__toolbar-label">
                                {TEXT.zoom} {Math.round(zoom * 100)}%
                            </span>
                            <button
                                type="button"
                                className="icon-button icon-button--compact"
                                onClick={handleZoomIn}
                                title={TEXT.zoomIn}
                            >
                                <span className="button__icon" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M6 12h12M12 6v12"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                            <button type="button" className="button button--ghost" onClick={handleFitWidth}>
                                {TEXT.fit}
                            </button>
                            <button type="button" className="button button--ghost" onClick={handleFullSize}>
                                {TEXT.fullSize}
                            </button>
                        </div>
                    </div>

                    <div
                        className="pdf-builder__stage"
                        ref={containerRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    >
                        {pageCanvas && (
                            <Stage
                                width={pageSize.width}
                                height={pageSize.height}
                                scaleX={zoom}
                                scaleY={zoom}
                                x={stagePosition.x}
                                y={stagePosition.y}
                                ref={stageRef}
                                onWheel={handleWheel}
                                onMouseDown={handleStageClick}
                                onMouseMove={handleStageMouseMove}
                                onMouseUp={handleStageMouseUp}
                                onTouchStart={handleStageClick}
                                onTouchMove={handleStageMouseMove}
                                onTouchEnd={handleStageMouseUp}
                            >
                                <Layer>
                                    <KonvaImage image={pageCanvas} listening={false} />
                                    {pageFields.map((field) => {
                                        const rect = normalizeField(field, pageSize);
                                        if (!rect) return null;
                                        const isSelected = selectedIds.includes(field.id);
                                        const isImage = field.type === 'image';
                                        const isRectangle = field.type === 'rectangle';
                                        const isReadOnlyText = field.type === 'text' && Boolean(field.isReadOnly);
                                        const readOnlyValue =
                                            isReadOnlyText && typeof field.staticValue === 'string'
                                                ? field.staticValue
                                                : '';
                                        const rectStyle = isRectangle ? normalizeRectStyle(field.styleJson) : null;
                                        const image = isImage ? getFieldImage(field) : null;
                                        const dragEnabled =
                                            isSelectTool && (selectedIds.length <= 1 || isSelected);
                                        const dragBounds = (pos) => {
                                            const width = rect.width;
                                            const height = rect.height;
                                            const nextX = snapEnabled ? snapValue(pos.x) : pos.x;
                                            const nextY = snapEnabled ? snapValue(pos.y) : pos.y;
                                            if (selectedIds.length > 1 && selectionBounds) {
                                                const minDx = -selectionBounds.minX;
                                                const maxDx = pageSize.width - selectionBounds.maxX;
                                                const minDy = -selectionBounds.minY;
                                                const maxDy = pageSize.height - selectionBounds.maxY;
                                                const dx = clamp(nextX - rect.x, minDx, maxDx);
                                                const dy = clamp(nextY - rect.y, minDy, maxDy);
                                                return {
                                                    x: rect.x + dx,
                                                    y: rect.y + dy,
                                                };
                                            }
                                            return {
                                                x: clamp(nextX, 0, pageSize.width - width),
                                                y: clamp(nextY, 0, pageSize.height - height),
                                            };
                                        };
                                        const attachRef = (node) => {
                                            shapeRefs.current[field.id] = node;
                                        };
                                        return (
                                            <React.Fragment key={field.id}>
                                                {isImage ? (
                                                    image ? (
                                                        <KonvaImage
                                                            x={rect.x}
                                                            y={rect.y}
                                                            width={rect.width}
                                                            height={rect.height}
                                                            image={image}
                                                            draggable={dragEnabled}
                                                            onClick={(event) => handleFieldSelect(field, event)}
                                                            onTap={(event) => handleFieldSelect(field, event)}
                                                            dragBoundFunc={dragBounds}
                                                            onDragStart={(event) => handleDragStart(field, event)}
                                                            onDragMove={(event) => handleDragMove(field, event)}
                                                            onDragEnd={(event) => handleDragEnd(field, event)}
                                                            onTransformEnd={(event) => handleTransformEnd(field, event)}
                                                            ref={attachRef}
                                                        />
                                                    ) : (
                                                        <Rect
                                                            x={rect.x}
                                                            y={rect.y}
                                                            width={rect.width}
                                                            height={rect.height}
                                                            fill="rgba(15,23,42,0.05)"
                                                            stroke="#94a3b8"
                                                            strokeWidth={1}
                                                            dash={[4, 4]}
                                                            draggable={dragEnabled}
                                                            onClick={(event) => handleFieldSelect(field, event)}
                                                            onTap={(event) => handleFieldSelect(field, event)}
                                                            dragBoundFunc={dragBounds}
                                                            onDragStart={(event) => handleDragStart(field, event)}
                                                            onDragMove={(event) => handleDragMove(field, event)}
                                                            onDragEnd={(event) => handleDragEnd(field, event)}
                                                            onTransformEnd={(event) => handleTransformEnd(field, event)}
                                                            ref={attachRef}
                                                        />
                                                    )
                                                ) : isRectangle ? (
                                                    <Rect
                                                        x={rect.x}
                                                        y={rect.y}
                                                        width={rect.width}
                                                        height={rect.height}
                                                        fill={rectStyle.fillEnabled ? rectStyle.fillColor : 'transparent'}
                                                        stroke={rectStyle.strokeColor}
                                                        strokeWidth={Math.max(1, Number(rectStyle.strokeWidth) || 1)}
                                                        opacity={Number.isFinite(rectStyle.opacity) ? rectStyle.opacity : 1}
                                                        draggable={dragEnabled}
                                                        onClick={(event) => handleFieldSelect(field, event)}
                                                        onTap={(event) => handleFieldSelect(field, event)}
                                                        dragBoundFunc={dragBounds}
                                                        onDragStart={(event) => handleDragStart(field, event)}
                                                        onDragMove={(event) => handleDragMove(field, event)}
                                                        onDragEnd={(event) => handleDragEnd(field, event)}
                                                        onTransformEnd={(event) => handleTransformEnd(field, event)}
                                                        ref={attachRef}
                                                    />
                                                ) : (
                                                    <Rect
                                                        x={rect.x}
                                                        y={rect.y}
                                                        width={rect.width}
                                                        height={rect.height}
                                                        fill={isSelected ? 'rgba(37,99,235,0.12)' : 'rgba(15,23,42,0.05)'}
                                                        stroke={isSelected ? '#2563eb' : '#94a3b8'}
                                                        strokeWidth={isSelected ? 2 : 1}
                                                        draggable={dragEnabled}
                                                        onClick={(event) => handleFieldSelect(field, event)}
                                                        onTap={(event) => handleFieldSelect(field, event)}
                                                        dragBoundFunc={dragBounds}
                                                        onDragStart={(event) => handleDragStart(field, event)}
                                                        onDragMove={(event) => handleDragMove(field, event)}
                                                        onDragEnd={(event) => handleDragEnd(field, event)}
                                                        onTransformEnd={(event) => handleTransformEnd(field, event)}
                                                        ref={attachRef}
                                                    />
                                                )}
                                                {isSelected && (isImage || isRectangle) && (
                                                    <Rect
                                                        x={rect.x}
                                                        y={rect.y}
                                                        width={rect.width}
                                                        height={rect.height}
                                                        stroke="#2563eb"
                                                        strokeWidth={2}
                                                        fill="transparent"
                                                        listening={false}
                                                    />
                                                )}
                                                {isReadOnlyText && readOnlyValue && (
                                                    <Text
                                                        x={rect.x + 6}
                                                        y={rect.y + 4}
                                                        width={Math.max(0, rect.width - 12)}
                                                        height={Math.max(0, rect.height - 8)}
                                                        text={readOnlyValue}
                                                        fontSize={Number(field.fontSize) || 12}
                                                        align={
                                                            field.align ||
                                                            (field.direction === 'rtl' ? 'right' : 'left')
                                                        }
                                                        verticalAlign="middle"
                                                        fill="#111827"
                                                        listening={false}
                                                    />
                                                )}
                                                {isSelected && (
                                                    <Label x={rect.x} y={Math.max(rect.y - 26, 0)}>
                                                        <Tag fill="#1f2937" cornerRadius={4} />
                                                        <Text
                                                            text={field.label || FIELD_TYPE_LABELS[field.type] || field.type}
                                                            fontSize={12}
                                                            padding={4}
                                                            fill="#fff"
                                                        />
                                                    </Label>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {selectionRect.visible && (
                                        <Rect
                                            x={selectionRect.x}
                                            y={selectionRect.y}
                                            width={selectionRect.width}
                                            height={selectionRect.height}
                                            fill="rgba(37,99,235,0.12)"
                                            stroke="#2563eb"
                                            dash={[4, 4]}
                                        />
                                    )}
                                    <Transformer
                                        ref={transformerRef}
                                        rotateEnabled={false}
                                        anchorSize={isCoarsePointer ? 14 : 4}
                                        boundBoxFunc={(oldBox, newBox) => newBox}
                                    />
                                </Layer>
                            </Stage>
                        )}
                    </div>
                </main>

                <aside className={`pdf-builder__sidebar pdf-builder__sidebar--properties${isRightCollapsed ? ' is-collapsed' : ''}`}>
                    <div className="pdf-builder__collapse">
                        <button
                            type="button"
                            className="icon-button icon-button--compact"
                            onClick={() => setIsRightCollapsed((prev) => !prev)}
                            title={isRightCollapsed ? TEXT.expandProps : TEXT.collapseProps}
                        >
                            <span className="button__icon" aria-hidden="true">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M15 6l-6 6 6 6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </span>
                            <span className="sr-only">
                                {isRightCollapsed ? TEXT.expandProps : TEXT.collapseProps}
                            </span>
                        </button>
                    </div>
                    {!isRightCollapsed && (
                        <div className="pdf-builder__panel">
                            <h3>{TEXT.properties}</h3>
                            {selectedFields.length === 0 && <p className="card__meta">{TEXT.emptySelection}</p>}

                            {selectedFields.length === 1 && (
                                <div className="stack">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.fieldLabel}</span>
                                    <input
                                        className="form-field__control"
                                        value={selectedFields[0].label || ''}
                                        onChange={(event) =>
                                            updateSelectedFields({ label: event.target.value })
                                        }
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.fieldKey}</span>
                                    <input
                                        className="form-field__control"
                                        value={selectedFields[0].key || ''}
                                        onChange={(event) =>
                                            updateSelectedFields({ key: event.target.value })
                                        }
                                    />
                                </label>
                                {singleSupportsSequence && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.sequenceNumber}</span>
                                        <input
                                            className="form-field__control"
                                            type="number"
                                            min="1"
                                            value={selectedFields[0].sequenceNumber ?? ''}
                                            onChange={(event) =>
                                                updateSelectedFields({
                                                    sequenceNumber: Number(event.target.value) || 0,
                                                })
                                            }
                                        />
                                    </label>
                                )}
                                {singleSupportsSequence && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.skipSequence}</span>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(selectedFields[0].skipSequence)}
                                            onChange={(event) =>
                                                updateSelectedFields({ skipSequence: event.target.checked })
                                            }
                                        />
                                    </label>
                                )}
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.section}</span>
                                    <select
                                        className="form-field__control"
                                        value={selectedFields[0].sectionId || ''}
                                        onChange={(event) =>
                                            updateSelectedFields({ sectionId: event.target.value || null })
                                        }
                                    >
                                        {sections.map((section) => (
                                            <option key={section.id} value={section.id}>
                                                {section.title}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {singleSupportsRequired && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.fieldRequired}</span>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(selectedFields[0].required)}
                                            onChange={(event) =>
                                                updateSelectedFields({ required: event.target.checked })
                                            }
                                        />
                                    </label>
                                )}
                                {supportsPlaceholder && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.placeholder}</span>
                                        <input
                                            className="form-field__control"
                                            value={selectedFields[0].placeholder || ''}
                                            onChange={(event) =>
                                                updateSelectedFields({ placeholder: event.target.value })
                                            }
                                            />
                                        </label>
                                )}
                                {selectedFields[0].type !== 'image' && selectedFields[0].type !== 'rectangle' && (
                                    <label className="form-field">
                                        <span className="form-field__label">Subtext</span>
                                        <textarea
                                            className="form-field__control form-field__control--textarea"
                                            rows={2}
                                            value={selectedFields[0].subtext || ''}
                                            onChange={(event) =>
                                                updateSelectedFields({ subtext: event.target.value })
                                            }
                                        />
                                    </label>
                                )}
                                {supportsAutoFill && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.autoFill}</span>
                                        <select
                                            className="form-field__control"
                                            value={selectedFields[0].autoFill || ''}
                                            onChange={(event) =>
                                                updateSelectedFields({ autoFill: event.target.value })
                                            }
                                        >
                                            {(AUTO_FILL_OPTIONS[singleSelectedType] || []).map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                {supportsTextOptions && (
                                    <>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.multiline}</span>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedFields[0].isMultiline)}
                                                onChange={(event) =>
                                                    updateSelectedFields({ isMultiline: event.target.checked })
                                                }
                                            />
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.readOnly}</span>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedFields[0].isReadOnly)}
                                                onChange={(event) =>
                                                    updateSelectedFields({ isReadOnly: event.target.checked })
                                                }
                                            />
                                        </label>
                                        {selectedFields[0].isReadOnly && (
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.staticValue}</span>
                                                <input
                                                    className="form-field__control"
                                                    value={selectedFields[0].staticValue || ''}
                                                    onChange={(event) =>
                                                        updateSelectedFields({ staticValue: event.target.value })
                                                    }
                                                />
                                            </label>
                                        )}
                                        {!selectedFields[0].isReadOnly && (
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.suggestedValues}</span>
                                                <textarea
                                                    className="form-field__control form-field__control--textarea"
                                                    rows={3}
                                                    value={selectedFields[0].suggestedValues || ''}
                                                    onChange={(event) =>
                                                        updateSelectedFields({ suggestedValues: event.target.value })
                                                    }
                                                />
                                            </label>
                                        )}
                                    </>
                                )}
                                {supportsTextStyle && (
                                    <>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.fontSize}</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                min="6"
                                                max="48"
                                                value={selectedFields[0].fontSize || 12}
                                                onChange={(event) => handleFontSizeChange(event.target.value)}
                                            />
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.fontSizeMode}</span>
                                            <select
                                                className="form-field__control"
                                                value={selectedFields[0].fontSizeMode || 'auto_fit'}
                                                onChange={(event) =>
                                                    updateSelectedFields({ fontSizeMode: event.target.value })
                                                }
                                            >
                                                <option value="manual">{TEXT.fontSizeModeManual}</option>
                                                <option value="auto_fit">{TEXT.fontSizeModeAuto}</option>
                                            </select>
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.wrapMode}</span>
                                            <select
                                                className="form-field__control"
                                                value={selectedFields[0].wrapMode || 'same_word'}
                                                onChange={(event) =>
                                                    updateSelectedFields({ wrapMode: event.target.value })
                                                }
                                            >
                                                <option value="same_line">{TEXT.wrapModeSameLine}</option>
                                                <option value="manual_lines">{TEXT.wrapModeManualLines}</option>
                                                <option value="same_word">{TEXT.wrapModeSameWord}</option>
                                                <option value="new_words">{TEXT.wrapModeNewWords}</option>
                                            </select>
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.align}</span>
                                            <select
                                                className="form-field__control"
                                                value={selectedFields[0].align || ''}
                                                onChange={(event) =>
                                                    updateSelectedFields({ align: event.target.value || '' })
                                                }
                                            >
                                                {ALIGN_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.direction}</span>
                                            <select
                                                className="form-field__control"
                                                value={selectedFields[0].direction || 'auto'}
                                                onChange={(event) =>
                                                    updateSelectedFields({ direction: event.target.value })
                                                }
                                            >
                                                {DIRECTION_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </>
                                )}
                                {(selectedFields[0].type === 'date' || selectedFields[0].type === 'time') && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.rangeMode}</span>
                                        <select
                                            className="form-field__control"
                                            value={selectedFields[0].rangeMode || 'single'}
                                            onChange={(event) =>
                                                updateSelectedFields({ rangeMode: event.target.value })
                                            }
                                        >
                                            <option value="single">{TEXT.rangeModeSingle}</option>
                                            <option value="range">{TEXT.rangeModeRange}</option>
                                        </select>
                                    </label>
                                )}
                                {selectedFields.length === 1 && pageSize.width && pageSize.height && (
                                    <>
                                        <div className="pdf-builder__metric-group">
                                            <span className="form-field__label">{TEXT.position}</span>
                                            <div className="pdf-builder__metric-grid">
                                                <label className="form-field form-field--compact">
                                                    <span className="form-field__label">{TEXT.x}</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={rectInputs.x}
                                                        onChange={(event) =>
                                                            handleRectInputChange('x', event.target.value)
                                                        }
                                                        onBlur={(event) => handleRectInputBlur('x', event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                event.currentTarget.blur();
                                                            }
                                                        }}
                                                    />
                                                </label>
                                                <label className="form-field form-field--compact">
                                                    <span className="form-field__label">{TEXT.y}</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={rectInputs.y}
                                                        onChange={(event) =>
                                                            handleRectInputChange('y', event.target.value)
                                                        }
                                                        onBlur={(event) => handleRectInputBlur('y', event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                event.currentTarget.blur();
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        <div className="pdf-builder__metric-group">
                                            <span className="form-field__label">{TEXT.size}</span>
                                            <div className="pdf-builder__metric-grid">
                                                <label className="form-field form-field--compact">
                                                    <span className="form-field__label">{TEXT.width}</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={rectInputs.width}
                                                        onChange={(event) =>
                                                            handleRectInputChange('width', event.target.value)
                                                        }
                                                        onBlur={(event) => handleRectInputBlur('width', event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                event.currentTarget.blur();
                                                            }
                                                        }}
                                                    />
                                                </label>
                                                <label className="form-field form-field--compact">
                                                    <span className="form-field__label">{TEXT.height}</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={rectInputs.height}
                                                        onChange={(event) =>
                                                            handleRectInputChange('height', event.target.value)
                                                        }
                                                        onBlur={(event) => handleRectInputBlur('height', event.target.value)}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter') {
                                                                event.currentTarget.blur();
                                                            }
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {selectedFields[0].type === 'image' && (
                                    <div className="stack">
                                        <span className="form-field__label">{TEXT.imageAsset}</span>
                                        {selectedFields[0].assetDataUrl ? (
                                            <div className="pdf-builder__image-preview">
                                                <img
                                                    src={selectedFields[0].assetDataUrl}
                                                    alt={selectedFields[0].label || TEXT.imageAsset}
                                                />
                                            </div>
                                        ) : (
                                            <span className="card__meta">{TEXT.imageUpload}</span>
                                        )}
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.imageUpload}</span>
                                            <input
                                                className="form-field__control"
                                                type="file"
                                                accept="image/*"
                                                onChange={(event) => handleImageUpload(event.target.files?.[0] || null)}
                                            />
                                        </label>
                                        {selectedFields[0].assetDataUrl && (
                                            <button
                                                type="button"
                                                className="button button--ghost"
                                                onClick={() => updateSelectedFields({ assetDataUrl: '' })}
                                            >
                                                {TEXT.imageClear}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {selectedFields[0].type === 'rectangle' && (
                                    <div className="stack">
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.rectStrokeColor}</span>
                                            <input
                                                className="form-field__control"
                                                type="color"
                                                value={selectedRectStyle.strokeColor}
                                                onChange={(event) =>
                                                    updateRectangleStyle({ strokeColor: event.target.value })
                                                }
                                            />
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.rectStrokeWidth}</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                min="0"
                                                step="1"
                                                value={selectedRectStyle.strokeWidth}
                                                onChange={(event) =>
                                                    updateRectangleStyle({ strokeWidth: Number(event.target.value) || 0 })
                                                }
                                            />
                                        </label>
                                        <label className="check-toggle">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedRectStyle.fillEnabled)}
                                                onChange={(event) =>
                                                    updateRectangleStyle({ fillEnabled: event.target.checked })
                                                }
                                            />
                                            <span>{TEXT.rectFill}</span>
                                        </label>
                                        {selectedRectStyle.fillEnabled && (
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.rectFillColor}</span>
                                                <input
                                                    className="form-field__control"
                                                    type="color"
                                                    value={selectedRectStyle.fillColor}
                                                    onChange={(event) =>
                                                        updateRectangleStyle({ fillColor: event.target.value })
                                                    }
                                                />
                                            </label>
                                        )}
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.rectOpacity}</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.1"
                                                value={selectedRectStyle.opacity}
                                                onChange={(event) =>
                                                    updateRectangleStyle({ opacity: Number(event.target.value) })
                                                }
                                            />
                                        </label>
                                    </div>
                                )}
                                {selectedFields[0].type === 'dropdown' && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.options}</span>
                                        <textarea
                                            className="form-field__control form-field__control--textarea"
                                            rows={4}
                                            value={
                                                typeof selectedFields[0].optionsJson === 'string'
                                                    ? selectedFields[0].optionsJson
                                                    : normalizeOptions(selectedFields[0].optionsJson).join('\n')
                                            }
                                            onChange={(event) =>
                                                updateSelectedFields({
                                                    optionsJson: event.target.value,
                                                })
                                            }
                                            onKeyDown={(event) => event.stopPropagation()}
                                        />
                                    </label>
                                )}
                                {selectedFields[0].type === 'number' && (
                                    <div className="stack">
                                        <span className="form-field__label">{TEXT.numericConfig}</span>
                                        <div className="stack">
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.min}</span>
                                                <input
                                                    className="form-field__control"
                                                    type="number"
                                                    value={selectedFields[0].numericConfigJson?.min ?? ''}
                                                    onChange={(event) =>
                                                        updateSelectedFields({
                                                            numericConfigJson: {
                                                                ...selectedFields[0].numericConfigJson,
                                                                min: event.target.value === '' ? null : Number(event.target.value),
                                                            },
                                                        })
                                                    }
                                                />
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.max}</span>
                                                <input
                                                    className="form-field__control"
                                                    type="number"
                                                    value={selectedFields[0].numericConfigJson?.max ?? ''}
                                                    onChange={(event) =>
                                                        updateSelectedFields({
                                                            numericConfigJson: {
                                                                ...selectedFields[0].numericConfigJson,
                                                                max: event.target.value === '' ? null : Number(event.target.value),
                                                            },
                                                        })
                                                    }
                                                />
                                            </label>
                                            <label className="form-field">
                                                <span className="form-field__label">{TEXT.decimals}</span>
                                                <input
                                                    className="form-field__control"
                                                    type="number"
                                                    min="0"
                                                    max="4"
                                                    value={selectedFields[0].numericConfigJson?.decimals ?? 0}
                                                    onChange={(event) =>
                                                        updateSelectedFields({
                                                            numericConfigJson: {
                                                                ...selectedFields[0].numericConfigJson,
                                                                decimals: Number(event.target.value) || 0,
                                                            },
                                                        })
                                                    }
                                                />
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedFields.length > 1 && (
                            <div className="stack">
                                {multiSupportsRequired && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.fieldRequired}</span>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(sharedValue('required'))}
                                            onChange={(event) =>
                                                updateSelectedFields({ required: event.target.checked })
                                            }
                                        />
                                    </label>
                                )}
                                {multiSupportsPlaceholder && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.placeholder}</span>
                                        <input
                                            className="form-field__control"
                                            value={sharedValue('placeholder') || ''}
                                            placeholder={TEXT.mixed}
                                            onChange={(event) =>
                                                updateSelectedFields({ placeholder: event.target.value })
                                            }
                                        />
                                    </label>
                                )}
                                {multiSupportsTextOptions && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.suggestedValues}</span>
                                        <textarea
                                            className="form-field__control"
                                            rows={4}
                                            value={sharedValue('suggestedValues') || ''}
                                            placeholder={TEXT.mixed}
                                            onChange={(event) =>
                                                updateSelectedFields({ suggestedValues: event.target.value })
                                            }
                                        />
                                    </label>
                                )}
                                {multiSupportsSection && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.section}</span>
                                        <select
                                            className="form-field__control"
                                            value={sharedValue('sectionId') || ''}
                                            onChange={(event) =>
                                                updateSelectedFields({ sectionId: event.target.value || null })
                                            }
                                        >
                                            <option value="" disabled>{TEXT.mixed}</option>
                                            {sections.map((section) => (
                                                <option key={section.id} value={section.id}>
                                                    {section.title}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                {multiSupportsSequence && (
                                    <label className="form-field">
                                        <span className="form-field__label">{TEXT.skipSequence}</span>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(sharedValue('skipSequence'))}
                                            onChange={(event) =>
                                                updateSelectedFields({ skipSequence: event.target.checked })
                                            }
                                        />
                                    </label>
                                )}
                                {multiSupportsTextStyle && (
                                    <>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.fontSize}</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                value={sharedValue('fontSize') || ''}
                                                placeholder={TEXT.mixed}
                                                onChange={(event) => handleFontSizeChange(event.target.value)}
                                            />
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.align}</span>
                                            <select
                                                className="form-field__control"
                                                value={sharedValue('align') || ''}
                                                onChange={(event) =>
                                                    updateSelectedFields({ align: event.target.value || '' })
                                                }
                                            >
                                                {ALIGN_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="form-field">
                                            <span className="form-field__label">{TEXT.direction}</span>
                                            <select
                                                className="form-field__control"
                                                value={sharedValue('direction') || 'auto'}
                                                onChange={(event) =>
                                                    updateSelectedFields({ direction: event.target.value })
                                                }
                                            >
                                                {DIRECTION_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </>
                                )}
                                <div className="stack">
                                    <span className="form-field__label">{TEXT.align}</span>
                                    <div className="pdf-builder__arrange-grid">
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('left')}
                                            title={TEXT.alignLeft}
                                            aria-label={TEXT.alignLeft}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignLeft}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('centerX')}
                                            title={TEXT.alignCenter}
                                            aria-label={TEXT.alignCenter}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignCenter}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('right')}
                                            title={TEXT.alignRight}
                                            aria-label={TEXT.alignRight}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignRight}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('top')}
                                            title={TEXT.alignTop}
                                            aria-label={TEXT.alignTop}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignTop}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('centerY')}
                                            title={TEXT.alignMiddle}
                                            aria-label={TEXT.alignMiddle}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignMiddle}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => alignSelection('bottom')}
                                            title={TEXT.alignBottom}
                                            aria-label={TEXT.alignBottom}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.alignBottom}
                                        </button>
                                    </div>
                                </div>
                                <div className="stack">
                                    <span className="form-field__label">{TEXT.position}</span>
                                    <div className="pdf-builder__metric-grid">
                                        <label className="form-field form-field--compact">
                                            <span className="form-field__label">X</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                inputMode="decimal"
                                                value={spacingInputs.x}
                                                onChange={(event) => handleSpacingChange('x', event.target.value)}
                                                disabled={!canSpaceSelection}
                                            />
                                        </label>
                                        <label className="form-field form-field--compact">
                                            <span className="form-field__label">Y</span>
                                            <input
                                                className="form-field__control"
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                inputMode="decimal"
                                                value={spacingInputs.y}
                                                onChange={(event) => handleSpacingChange('y', event.target.value)}
                                                disabled={!canSpaceSelection}
                                            />
                                        </label>
                                    </div>
                                    <div className="pdf-builder__arrange-grid pdf-builder__arrange-grid--two">
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => distributeSelection('x')}
                                            title={TEXT.distributeX}
                                            aria-label={TEXT.distributeX}
                                            disabled={!canDistributeSelection}
                                        >
                                            {TEXT.distributeX}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => distributeSelection('y')}
                                            title={TEXT.distributeY}
                                            aria-label={TEXT.distributeY}
                                            disabled={!canDistributeSelection}
                                        >
                                            {TEXT.distributeY}
                                        </button>
                                    </div>
                                </div>
                                <div className="stack">
                                    <span className="form-field__label">{TEXT.size}</span>
                                    <div className="pdf-builder__arrange-grid pdf-builder__arrange-grid--two">
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => matchSelectionSize('width')}
                                            title={TEXT.matchWidth}
                                            aria-label={TEXT.matchWidth}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.matchWidth}
                                        </button>
                                        <button
                                            type="button"
                                            className="pdf-builder__arrange-button"
                                            onClick={() => matchSelectionSize('height')}
                                            title={TEXT.matchHeight}
                                            aria-label={TEXT.matchHeight}
                                            disabled={!canAlignSelection}
                                        >
                                            {TEXT.matchHeight}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {supportsLayerOrdering && (
                            <div className="stack">
                                <span className="form-field__label">{TEXT.layer}</span>
                                <div className="pdf-builder__arrange-grid pdf-builder__arrange-grid--two">
                                    <button
                                        type="button"
                                        className="pdf-builder__arrange-button"
                                        onClick={() => moveSelectionLayer('front')}
                                    >
                                        {TEXT.bringToFront}
                                    </button>
                                    <button
                                        type="button"
                                        className="pdf-builder__arrange-button"
                                        onClick={() => moveSelectionLayer('back')}
                                    >
                                        {TEXT.sendToBack}
                                    </button>
                                </div>
                            </div>
                        )}
                        {activeTool === 'tableRow' && selectedFields.length > 0 && (
                            <div className="stack">
                                <span className="form-field__label">{TEXT.tableRows}</span>
                                <div className="pdf-builder__metric-grid">
                                    <label className="form-field form-field--compact">
                                        <span className="form-field__label">{TEXT.rowCount}</span>
                                        <input
                                            className="form-field__control"
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={tableRowConfig.count}
                                            onChange={(event) =>
                                                handleTableRowConfigChange('count', event.target.value)
                                            }
                                        />
                                    </label>
                                    <label className="form-field form-field--compact">
                                        <span className="form-field__label">{TEXT.rowSpacing}</span>
                                        <input
                                            className="form-field__control"
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            inputMode="decimal"
                                            value={tableRowConfig.spacing}
                                            onChange={(event) =>
                                                handleTableRowConfigChange('spacing', event.target.value)
                                            }
                                        />
                                    </label>
                                </div>
                                <button
                                    type="button"
                                    className="button button--light"
                                    onClick={handleCreateTableRows}
                                    disabled={!canApplyTableRows}
                                >
                                    {TEXT.applyRows}
                                </button>
                            </div>
                        )}
                    </div>
                    )}
                </aside>
            </div>
            {isExportOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            setIsExportOpen(false);
                        }
                    }}
                >
                    <div className="modal-content" role="document">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.exportTitle}</h2>
                        </header>
                        <div className="modal-form">
                            <div className="modal-form__body">
                                <textarea
                                    className="form-field__control form-field__control--textarea json-textarea"
                                    rows={8}
                                    value={exportPayload}
                                    readOnly
                                    dir="ltr"
                                />
                            </div>
                            <footer className="modal-form__footer">
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={() => setIsExportOpen(false)}
                                >
                                    {TEXT.close}
                                </button>
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={handleCopyExport}
                                >
                                    {TEXT.exportCopy}
                                </button>
                            </footer>
                        </div>
                    </div>
                </div>
            )}
            {isImportOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            setIsImportOpen(false);
                        }
                    }}
                >
                    <div className="modal-content" role="document">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.importTitle}</h2>
                            <p className="card__meta">{TEXT.importHint}</p>
                        </header>
                        <div className="modal-form">
                            <div className="modal-form__body">
                                <textarea
                                    className="form-field__control form-field__control--textarea json-textarea"
                                    rows={8}
                                    placeholder={TEXT.importPlaceholder}
                                    value={importPayload}
                                    onChange={(event) => setImportPayload(event.target.value)}
                                    dir="ltr"
                                />
                            </div>
                            {importError && <div className="alert alert--danger">{importError}</div>}
                            <footer className="modal-form__footer">
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={() => setIsImportOpen(false)}
                                >
                                    {TEXT.close}
                                </button>
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={handleImportSchema}
                                >
                                    {TEXT.importAction}
                                </button>
                            </footer>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
